import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAccessToken, setSessionToken } from '../auth/useAccessToken';
import { apiBaseUrl } from '../auth/config';
import { apiFetch, apiFetchBlob } from './client';
import type { ApiError } from './client';
import type {
  AuthorizationListRow,
  AuthorizationStatus,
  BootstrapStatus,
  BootstrapCompleteRequest,
  BootstrapCompleteResponse,
  CreateIssuePayload,
  EditIssuePayload,
  EffectivePermissions,
  FullLoginResponse,
  Issue,
  LoginRequest,
  LoginResponse,
  TotpSetupResponse,
  UserDetailRole,
  Me,
  Operator,
  OperatorBranding,
  Permission,
  MachineDetail,
  MachineMaintenanceEntry,
  MachineMeterSnapshot,
  MachineSummary,
  MachineVersion,
  DailyMeterRow,
  MachineMeterQuarantine,
  ResolveMeterQuarantineResult,
  MachineEventRow,
  AccessWindowOpenResult,
  OpenAccessWindow,
  RolePermissionRow,
  RoleSummary,
  EffectiveSetting,
  SettingDefinition,
  IotQrBatchSummary,
  IotQrPairSummary,
  MachineDeviceSummary,
  MachineDeviceFirmware,
  FirmwareSkew,
  FirmwareRolloutPauseState,
  StartConsoleSessionResponse,
  ConsoleFramesResponse,
  MachineQrBatchSummary,
  MachineQrPairSummary,
  QrBatchSummary,
  QrPairSummary,
  SoftwareReleaseSummary,
  SiteControllerDetail,
  SiteControllerSummary,
  PendingReissueRow,
  BreakGlassResponse,
  CaStatusResponse,
  CaRotateResponse,
  CommissioningRetireResponse,
  SiteDetail,
  SiteSummary,
  SupplierDetail,
  SupplierSummary,
  UserDetail,
  UserCreateRequest,
  UserCreateResponse,
  LoginAuditRow,
  AuditLogRow,
  StickerBatch,
  StickerRow,
  CreateBatchRequest,
  StickerLabelTemplate,
  StickerLabelTemplateBody,
  StickerHeaderTemplate,
  UserSummary,
} from './types';

export function useBootstrapStatus() {
  return useQuery({
    queryKey: ['bootstrap', 'status'],
    queryFn: () => apiFetch<BootstrapStatus>('/bootstrap/status'),
    staleTime: 0,
  });
}

export function useCompleteBootstrap() {
  return useMutation({
    mutationFn: (body: BootstrapCompleteRequest) =>
      apiFetch<BootstrapCompleteResponse>('/bootstrap/complete', {
        method: 'POST',
        body: JSON.stringify({
          Username:    body.username,
          Email:       body.email,
          DisplayName: body.displayName,
          Password:    body.password,
          Locale:      body.locale,
          TimeZone:    body.timeZone,
        }),
      }),
  });
}

// ---------- /auth/* — login + TOTP handshake ----------
//
// These hooks talk to the unauthenticated/temp-token endpoints. The full
// login token they yield is stored in sessionStorage (see useAccessToken)
// so the rest of the API layer attaches it automatically.

export function useLogin() {
  return useMutation({
    mutationFn: (body: LoginRequest) =>
      apiFetch<LoginResponse>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({
          UsernameOrEmail: body.usernameOrEmail,
          Password:        body.password,
        }),
      }),
  });
}

export function useSetupTotp() {
  return useMutation({
    mutationFn: (tempToken: string) =>
      apiFetch<TotpSetupResponse>('/auth/setup-totp', {
        method: 'POST',
      }, async () => tempToken),
  });
}

export function useConfirmTotp() {
  return useMutation({
    mutationFn: ({ tempToken, code }: { tempToken: string; code: string }) =>
      apiFetch<{ message: string; backup_codes: string[] }>('/auth/confirm-totp', {
        method: 'POST',
        body: JSON.stringify({ Code: code }),
      }, async () => tempToken),
  });
}

export function useVerifyTotp() {
  return useMutation({
    mutationFn: ({ tempToken, code }: { tempToken: string; code: string }) =>
      apiFetch<FullLoginResponse>('/auth/verify-totp', {
        method: 'POST',
        body: JSON.stringify({ Code: code }),
      }, async () => tempToken),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: ({ tempToken, currentPassword, newPassword }: {
      tempToken: string; currentPassword: string; newPassword: string;
    }) =>
      apiFetch<void>('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({
          CurrentPassword: currentPassword,
          NewPassword:     newPassword,
        }),
      }, async () => tempToken),
  });
}

export function useMe() {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['me'],
    queryFn: () => apiFetch<Me>('/api/v1/me', {}, getToken),
    staleTime: 30_000,
  });
}

export function useMyPermissions() {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['me', 'permissions'],
    queryFn: () =>
      apiFetch<EffectivePermissions>('/api/v1/me/effective-permissions', {}, getToken),
    staleTime: 30_000,
  });
}

export function useOperatorsList() {
  const getToken = useAccessToken();
  return {
    queryKey: ['operators'] as const,
    listFn: () => apiFetch<Operator[]>('/api/v1/operators', {}, getToken),
  };
}

export function useLoginAuditList() {
  const getToken = useAccessToken();
  return {
    queryKey: ['audit', 'login'] as const,
    listFn: () => apiFetch<LoginAuditRow[]>('/api/v1/audit/login?take=200', {}, getToken),
  };
}

/** A single machine's audit trail (restarts, status changes, lifecycle). RLS
 *  scopes operators to their own; ARMS sees all. Only enabled when a machine is
 *  open and the caller holds audit.view. */
export function useMachineAudit(machineId: string | null, enabled: boolean) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['audit', 'machine', machineId],
    enabled: enabled && !!machineId,
    queryFn: () => apiFetch<AuditLogRow[]>(
      `/api/v1/audit/log?subjectResourceType=machine&subjectResourceId=${machineId}&take=200`, {}, getToken),
  });
}

/**
 * Per-user audit history. Pulls BOTH the sign-in stream
 * (`/api/v1/audit/login?userId=…`) and the activity stream
 * (`/api/v1/audit/log?actorUserId=…`) in parallel and merges them
 * into a single timeline sorted descending by `occurredAt`.
 *
 * The two endpoints have different row shapes — login rows know about
 * `factorUsed` / `ipAddress` / `userAgent`, activity rows know about
 * `subjectResourceType` / `subjectDisplayName` / `outcome` / `reason`.
 * We project both into a discriminated `UserAuditEvent` so the grid
 * can render either kind with the right columns.
 *
 * Take=500 per side gives ops enough history to investigate lockouts
 * and "what did this user actually do" questions without paging.
 */
export function useUserAuditHistory(userId: string | undefined) {
  const getToken = useAccessToken();
  return {
    queryKey: ['audit', 'history', 'user', userId ?? ''] as const,
    listFn: async (): Promise<UserAuditEvent[]> => {
      const id = encodeURIComponent(userId ?? '');
      const [logins, activity] = await Promise.all([
        apiFetch<LoginAuditRow[]>(
          `/api/v1/audit/login?userId=${id}&take=500`, {}, getToken),
        apiFetch<AuditLogRow[]>(
          `/api/v1/audit/log?actorUserId=${id}&take=500`, {}, getToken),
      ]);
      const merged: UserAuditEvent[] = [
        ...logins.map((r): UserAuditEvent => ({
          id:              `login:${r.id}`,
          kind:            'login',
          occurredAt:      r.occurredAt,
          eventType:       r.eventType,
          outcome:         loginOutcomeOf(r.eventType),
          correlationId:   r.correlationId,
          factorUsed:      r.factorUsed,
          ipAddress:       r.ipAddress,
          userAgent:       r.userAgent,
          geoipCountry:    r.geoipCountry,
          outcomeDetail:   r.outcomeDetail,
        })),
        ...activity.map((r): UserAuditEvent => ({
          id:                  `activity:${r.id}`,
          kind:                'activity',
          occurredAt:          r.occurredAt,
          eventType:           r.eventType,
          outcome:             r.outcome,
          correlationId:       r.correlationId,
          tier:                r.tier,
          subjectResourceType: r.subjectResourceType,
          subjectResourceId:   r.subjectResourceId,
          subjectDisplayName:  r.subjectDisplayName,
          actorDisplayName:    r.actorDisplayName,
          reason:              r.reason,
        })),
      ];
      merged.sort((a, b) => (a.occurredAt < b.occurredAt ? 1 : a.occurredAt > b.occurredAt ? -1 : 0));
      return merged;
    },
  };
}

// Login rows don't carry an explicit outcome column — derive it from the
// event type so the timeline can colour-code sign-in failures the same
// way activity failures are coloured.
function loginOutcomeOf(eventType: string): string {
  if (eventType.endsWith('.failure') || eventType.endsWith('.failed')) return 'failure';
  if (eventType === 'login.success'
   || eventType === 'login.password_ok'
   || eventType === 'totp.setup_confirmed'
   || eventType === 'password.changed') return 'success';
  return 'info';
}

export type UserAuditEvent = {
  /** Discriminator-tagged composite id so React-Query / DataGrid dedupe across kinds. */
  id: string;
  kind: 'login' | 'activity';
  occurredAt: string;
  eventType: string;
  outcome: string;
  correlationId: string;
  // login-only
  factorUsed?: string | null;
  ipAddress?: string | null;
  userAgent?: string;
  geoipCountry?: string | null;
  outcomeDetail?: string | null;
  // activity-only
  tier?: 'operator' | 'platform';
  subjectResourceType?: string | null;
  subjectResourceId?: string | null;
  subjectDisplayName?: string | null;
  actorDisplayName?: string | null;
  reason?: string | null;
};

export function useAuditLogList() {
  const getToken = useAccessToken();
  return {
    queryKey: ['audit', 'log'] as const,
    listFn: () => apiFetch<AuditLogRow[]>('/api/v1/audit/log?take=200', {}, getToken),
  };
}

// ---------- Stickers ----------

export function useStickerBatchesList() {
  const getToken = useAccessToken();
  return {
    queryKey: ['sticker-batches'] as const,
    listFn: () => apiFetch<StickerBatch[]>('/api/v1/sticker-batches', {}, getToken),
  };
}

export function useStickerBatch(batchId: string | null) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['sticker-batch', batchId],
    queryFn: () => apiFetch<StickerBatch>(`/api/v1/sticker-batches/${batchId}`, {}, getToken),
    enabled: !!batchId,
  });
}

export function useStickerBatchStickers(batchId: string | null) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['sticker-batch', batchId, 'stickers'],
    queryFn: () => apiFetch<StickerRow[]>(`/api/v1/sticker-batches/${batchId}/stickers`, {}, getToken),
    enabled: !!batchId,
  });
}

export function useCreateStickerBatch() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBatchRequest) =>
      apiFetch<{ batch_id: string; batch_code: string; count: number }>(
        '/api/v1/sticker-batches', {
          method: 'POST',
          body: JSON.stringify(body),
        }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sticker-batches'] }),
  });
}

export function useVoidStickerBatch() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ batchId, reason }: { batchId: string; reason?: string }) =>
      apiFetch<void>(`/api/v1/sticker-batches/${batchId}/void`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }, getToken),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sticker-batches'] });
      qc.invalidateQueries({ queryKey: ['sticker-batch'] });
    },
  });
}

export function useVoidSticker() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stickerId, reason }: { stickerId: string; reason?: string }) =>
      apiFetch<void>(`/api/v1/stickers/${stickerId}/void`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sticker-batch'] }),
  });
}

export function useMarkStickerBatchShipped() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (batchId: string) =>
      apiFetch<void>(`/api/v1/sticker-batches/${batchId}/mark-shipped`, {
        method: 'POST',
      }, getToken),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sticker-batches'] });
      qc.invalidateQueries({ queryKey: ['sticker-batch'] });
    },
  });
}

export function useMarkStickerBatchReceived() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (batchId: string) =>
      apiFetch<void>(`/api/v1/sticker-batches/${batchId}/mark-received`, {
        method: 'POST',
      }, getToken),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sticker-batches'] });
      qc.invalidateQueries({ queryKey: ['sticker-batch'] });
    },
  });
}

export function useRetireSticker() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stickerId, reason }: { stickerId: string; reason?: string }) =>
      apiFetch<void>(`/api/v1/stickers/${stickerId}/retire`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sticker-batch'] }),
  });
}

// ---------- Sticker label templates ----------

export function useStickerLabelTemplatesList() {
  const getToken = useAccessToken();
  return {
    queryKey: ['sticker-label-templates'] as const,
    listFn: () => apiFetch<StickerLabelTemplate[]>('/api/v1/sticker-label-templates', {}, getToken),
  };
}

export function useStickerLabelTemplates() {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['sticker-label-templates'] as const,
    queryFn: () => apiFetch<StickerLabelTemplate[]>('/api/v1/sticker-label-templates', {}, getToken),
    staleTime: 60_000,
  });
}

export function useCreateStickerLabelTemplate() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: StickerLabelTemplateBody) =>
      apiFetch<StickerLabelTemplate>('/api/v1/sticker-label-templates', {
        method: 'POST',
        body: JSON.stringify(body),
      }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sticker-label-templates'] }),
  });
}

export function useUpdateStickerLabelTemplate() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: StickerLabelTemplateBody }) =>
      apiFetch<StickerLabelTemplate>(`/api/v1/sticker-label-templates/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sticker-label-templates'] }),
  });
}

export function useDeleteStickerLabelTemplate() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/v1/sticker-label-templates/${id}`, {
        method: 'DELETE',
      }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sticker-label-templates'] }),
  });
}

// ---------- Sticker header templates ----------

export function useStickerHeaderTemplatesList() {
  const getToken = useAccessToken();
  return {
    queryKey: ['sticker-header-templates'] as const,
    listFn: () => apiFetch<StickerHeaderTemplate[]>('/api/v1/sticker-header-templates', {}, getToken),
  };
}

export function useStickerHeaderTemplates() {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['sticker-header-templates'] as const,
    queryFn: () => apiFetch<StickerHeaderTemplate[]>('/api/v1/sticker-header-templates', {}, getToken),
    staleTime: 60_000,
  });
}

export type StickerHeaderTemplateFormValues = {
  name: string;
  headerConfigJson: string;        // serialised StickerHeaderConfig
  logoSource: 'none' | 'arms' | 'operator' | 'custom';
  logoOperatorId?: string | null;
  logoFile?: File | null;
  notes?: string | null;
};

function buildHeaderTemplateFormData(values: StickerHeaderTemplateFormValues) {
  const fd = new FormData();
  fd.set('name', values.name);
  fd.set('headerConfig', values.headerConfigJson);
  fd.set('logoSource', values.logoSource);
  if (values.logoOperatorId) fd.set('logoOperatorId', values.logoOperatorId);
  if (values.notes != null) fd.set('notes', values.notes);
  if (values.logoFile)      fd.set('logo', values.logoFile);
  return fd;
}

async function multipartHeaderTemplate(
  url: string, method: 'POST' | 'PUT', token: string | null,
  values: StickerHeaderTemplateFormValues,
): Promise<StickerHeaderTemplate> {
  const resp = await fetch(url, {
    method,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: buildHeaderTemplateFormData(values),
  });
  if (!resp.ok) {
    // Always include METHOD + path + status + body so the user sees
    // exactly which endpoint failed and why. "Not found" alone is not
    // useful — they need to know what wasn't found.
    const text = await resp.text().catch(() => '');
    let detail = text;
    try {
      const obj = JSON.parse(text) as { message?: string; code?: string };
      if (obj?.message) detail = obj.message;
      else if (obj?.code) detail = obj.code;
    } catch { /* not JSON */ }
    // Strip the apiBaseUrl from the URL when present so the path is
    // readable without leaking origin twice.
    const path = url.replace(apiBaseUrl, '') || url;
    const status = `HTTP ${resp.status}${resp.statusText ? ' ' + resp.statusText : ''}`;
    const tail   = detail ? ` — ${detail}` : '';
    throw new Error(`${method} ${path}: ${status}${tail}`);
  }
  // Tolerate an empty 200/201 body just in case.
  const text = await resp.text();
  return (text ? JSON.parse(text) : {}) as StickerHeaderTemplate;
}

export function useCreateStickerHeaderTemplate() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (values: StickerHeaderTemplateFormValues) => {
      const token = await getToken();
      return multipartHeaderTemplate(
        `${apiBaseUrl}/api/v1/sticker-header-templates`, 'POST', token, values);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sticker-header-templates'] }),
  });
}

export function useUpdateStickerHeaderTemplate() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, values }: { id: string; values: StickerHeaderTemplateFormValues }) => {
      const token = await getToken();
      return multipartHeaderTemplate(
        `${apiBaseUrl}/api/v1/sticker-header-templates/${id}`, 'PUT', token, values);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sticker-header-templates'] }),
  });
}

export function useDeleteStickerHeaderTemplate() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/v1/sticker-header-templates/${id}`, {
        method: 'DELETE',
      }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sticker-header-templates'] }),
  });
}

export function useApplyStickerHeaderToBatch() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ batchId, templateId }: { batchId: string; templateId: string }) =>
      apiFetch<void>(`/api/v1/sticker-batches/${batchId}/header-template`, {
        method: 'PUT',
        body: JSON.stringify({ templateId }),
      }, getToken),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sticker-batches'] });
      qc.invalidateQueries({ queryKey: ['sticker-batch'] });
    },
  });
}

export function useClearStickerHeaderFromBatch() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (batchId: string) =>
      apiFetch<void>(`/api/v1/sticker-batches/${batchId}/header-template`, {
        method: 'DELETE',
      }, getToken),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sticker-batches'] });
      qc.invalidateQueries({ queryKey: ['sticker-batch'] });
    },
  });
}

/**
 * URL the browser opens to download the print-ready PDF. Token must be
 * passed via query param since `<a target="_blank">` can't carry headers.
 */
export function stickerBatchPrintUrl(batchId: string, opts?: {
  paper?: string; widthMm?: number; heightMm?: number;
  cols?: number; rows?: number;
  marginLeftMm?: number; marginRightMm?: number;
  marginTopMm?: number;  marginBottomMm?: number;
  gutterMm?: number;
}) {
  const qs = new URLSearchParams();
  if (opts?.paper)            qs.set('paper',          opts.paper);
  if (opts?.widthMm)          qs.set('widthMm',        String(opts.widthMm));
  if (opts?.heightMm)         qs.set('heightMm',       String(opts.heightMm));
  if (opts?.cols)             qs.set('cols',           String(opts.cols));
  if (opts?.rows)             qs.set('rows',           String(opts.rows));
  // Margins / gutter use != null so 0mm is preserved (the renderer
  // distinguishes "use default 5mm" from "explicit 0mm").
  if (opts?.marginLeftMm   != null) qs.set('marginLeftMm',   String(opts.marginLeftMm));
  if (opts?.marginRightMm  != null) qs.set('marginRightMm',  String(opts.marginRightMm));
  if (opts?.marginTopMm    != null) qs.set('marginTopMm',    String(opts.marginTopMm));
  if (opts?.marginBottomMm != null) qs.set('marginBottomMm', String(opts.marginBottomMm));
  if (opts?.gutterMm       != null) qs.set('gutterMm',       String(opts.gutterMm));
  const q = qs.toString();
  return `/api/v1/sticker-batches/${batchId}/print${q ? '?' + q : ''}`;
}

export function useMyPreferences() {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['me', 'preferences'],
    queryFn: () => apiFetch<Record<string, unknown>>('/api/v1/me/preferences', {}, getToken),
    staleTime: 60_000,
  });
}

export function useUpdatePreferences() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (next: Record<string, unknown>) =>
      apiFetch<void>('/api/v1/me/preferences', {
        method: 'PUT',
        body: JSON.stringify(next),
      }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'preferences'] }),
  });
}

export function useOperatorBranding(operatorId: string | null | undefined) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['operators', operatorId, 'branding'],
    queryFn: () => apiFetch<OperatorBranding>(`/api/v1/operators/${operatorId}/branding`, {}, getToken),
    staleTime: 60_000,
    enabled: !!operatorId,
  });
}

/**
 * Authenticated object-URL for an operator's logo.
 *
 * `GET /api/v1/operators/{id}/logo` is bearer-auth-only. An HTML `<img src>`
 * can't send the Authorization header, so a direct `<img src=".../logo">`
 * 401s and the logo never shows. This hook fetches the bytes WITH the token
 * (via {@link apiFetchBlob}), wraps the resulting Blob in an object-URL, and
 * returns that URL for `<img src>`. Returns `null` while there's no logo
 * (so the caller renders nothing and no request is issued) or before the
 * blob has loaded.
 *
 * Gating: pass `hasLogo` from {@link useOperatorBranding} so a logo-less
 * operator never issues a request that would 404 / 401.
 *
 * Object-URL lifecycle is managed here: each new Blob mints a fresh URL and
 * revokes the previous one; the final URL is revoked on unmount. Cache
 * invalidation of `['operators', operatorId, 'logo-blob']` drives a refetch
 * (e.g. after an upload), so no `?t=` cache-buster is needed.
 */
export function useOperatorLogoUrl(
  operatorId: string | null | undefined,
  hasLogo: boolean | undefined,
): string | null {
  const getToken = useAccessToken();
  const query = useQuery({
    queryKey: ['operators', operatorId, 'logo-blob'],
    queryFn: () => apiFetchBlob(`/api/v1/operators/${operatorId}/logo`, getToken),
    enabled: !!operatorId && !!hasLogo,
    staleTime: 60_000,
  });

  const blob = query.data ?? null;
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!blob) {
      setObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    setObjectUrl(url);
    return () => { URL.revokeObjectURL(url); };
  }, [blob]);

  return objectUrl;
}

export function useUsersList(operatorId?: string | null, siteId?: string | null) {
  const getToken = useAccessToken();
  const qs = new URLSearchParams({ take: '500' });
  if (operatorId) qs.set('operatorId', operatorId);
  if (siteId) qs.set('siteId', siteId);
  return {
    queryKey: ['users', operatorId ?? null, siteId ?? null] as const,
    listFn: () => apiFetch<UserSummary[]>(`/api/v1/users?${qs}`, {}, getToken),
  };
}

export function useUserDetail(userId: string | null) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['users', 'detail', userId],
    queryFn: () => apiFetch<UserDetail>(`/api/v1/users/${userId}`, {}, getToken),
    enabled: !!userId,
  });
}

export function useAddUser() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UserCreateRequest) =>
      apiFetch<UserCreateResponse>('/api/v1/users', {
        method: 'POST',
        body: JSON.stringify({
          Username:    body.username || null,
          Email:       body.email || null,
          DisplayName: body.displayName,
          OperatorId:  body.operatorId ?? null,
          IsArms:      body.isArms ?? false,
          IsSystem:    body.isSystem ?? false,
          Locale:      body.locale,
          TimeZone:    body.timeZone,
        }),
      }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useResetOwn2fa() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (totpCode: string) =>
      apiFetch<void>('/api/v1/me/2fa/reset', {
        method: 'POST',
        body: JSON.stringify({ Code: totpCode }),
      }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

export function useRegenerateBackupCodes() {
  const getToken = useAccessToken();
  return useMutation({
    mutationFn: (totpCode: string) =>
      apiFetch<{ backup_codes: string[] }>('/api/v1/me/2fa/regenerate-backup-codes', {
        method: 'POST',
        body: JSON.stringify({ Code: totpCode }),
      }, getToken),
  });
}

export function useBackupCodesStatus(enabled = true) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['me', '2fa', 'backup-codes-status'],
    queryFn: () => apiFetch<{ unused_count: number }>(
      '/api/v1/me/2fa/backup-codes/status', {}, getToken),
    enabled,
  });
}

export function useStartImpersonation() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ operatorId, roleId }: { operatorId: string; roleId: string }) =>
      apiFetch<{ token: string; expires_at: string; operator_id: string;
                 operator_code: string; operator_display_name: string;
                 role_id: string; role_code: string; role_display_name: string }>(
        '/api/v1/me/impersonate', {
          method: 'POST',
          body: JSON.stringify({ OperatorId: operatorId, RoleId: roleId }),
        }, getToken),
    onSuccess: (data) => {
      // Store the new impersonation token BEFORE invalidating so every
      // refetch the invalidation triggers carries the new claim. Storing
      // it after invalidateQueries (the old ordering) raced a stale /me
      // refetch against the pre-impersonation token → impersonated_operator_id
      // came back null even though the banner was up (issue #49 bug B).
      setSessionToken(data.token);
      qc.invalidateQueries();
    },
  });
}

export function useStopImpersonation() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ token: string; expires_at: string }>(
        '/api/v1/me/stop-impersonation', { method: 'POST' }, getToken),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useEditUser(userId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      displayName?: string;
      username?: string;
      email?: string | null;
      locale?: string;
      timeZone?: string;
      phone?: string | null;
    }) =>
      apiFetch<{ user_id: string }>(`/api/v1/users/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          DisplayName: body.displayName,
          Username:    body.username,
          Email:       body.email,
          Locale:      body.locale,
          TimeZone:    body.timeZone,
          Phone:       body.phone,
        }),
      }, getToken),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['users', 'detail', userId] });
    },
  });
}

export function useUserAction() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, action, reason }: {
      userId: string;
      action: 'suspend' | 'reactivate' | 'deactivate';
      reason?: string;
    }) =>
      apiFetch<void>(
        `/api/v1/users/${userId}/${action}`,
        {
          method: 'POST',
          ...(reason !== undefined ? { body: JSON.stringify({ reason }) } : {}),
        },
        getToken,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

// ---------- Admin user-actions (TablePot parity) ----------

export function useAdminResetPassword(userId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ temporaryPassword: string }>(`/api/v1/users/${userId}/reset-password`,
        { method: 'POST' }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useAdminResetTotp(userId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<void>(`/api/v1/users/${userId}/reset-2fa`, { method: 'POST' }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useAdminResetLoginAttempts(userId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<void>(`/api/v1/users/${userId}/reset-login-attempts`, { method: 'POST' }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useAdminClearPasswordHistory(userId: string) {
  const getToken = useAccessToken();
  return useMutation({
    mutationFn: () =>
      apiFetch<void>(`/api/v1/users/${userId}/reset-password-history`, { method: 'POST' }, getToken),
  });
}

export function useChangeOwnPassword() {
  const getToken = useAccessToken();
  return useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      apiFetch<void>('/api/v1/me/change-password', {
        method: 'POST',
        body: JSON.stringify({
          CurrentPassword: body.currentPassword,
          NewPassword:     body.newPassword,
        }),
      }, getToken),
  });
}

// V8 flatten — direct user-to-role and user-to-site assignment.
// Group/membership/scope-binding plumbing has been removed.

export function useUserRoles(userId: string | null) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['users', userId, 'roles'] as const,
    queryFn: () => apiFetch<UserDetailRole[]>(`/api/v1/users/${userId}/roles`, {}, getToken),
    enabled: !!userId,
  });
}

export function useSetUserRoles() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, roleIds }: { userId: string; roleIds: string[] }) =>
      apiFetch<void>(`/api/v1/users/${userId}/roles`, {
        method: 'PUT',
        body: JSON.stringify({ RoleIds: roleIds }),
      }, getToken),
    onSuccess: (_v, { userId }) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['users', userId] });
      qc.invalidateQueries({ queryKey: ['users', userId, 'roles'] });
    },
  });
}

export function useUserSites(userId: string | null) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['users', userId, 'sites'] as const,
    queryFn: () => apiFetch<string[]>(`/api/v1/users/${userId}/sites`, {}, getToken),
    enabled: !!userId,
  });
}

export function useSetUserSites() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, siteIds }: { userId: string; siteIds: string[] }) =>
      apiFetch<void>(`/api/v1/users/${userId}/sites`, {
        method: 'PUT',
        body: JSON.stringify({ SiteIds: siteIds }),
      }, getToken),
    onSuccess: (_v, { userId }) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['users', userId] });
      qc.invalidateQueries({ queryKey: ['users', userId, 'sites'] });
    },
  });
}

/**
 * Roles list for any caller.
 *
 * @param targetOperatorId  ACES-only — when set, the response also
 *   includes that operator's clones so an ACES admin can assign
 *   per-operator roles in the Add / Edit User dialogs. Operator
 *   callers ignore this parameter server-side; they're always clipped
 *   to their own operator.
 */
export function useRolesList(targetOperatorId?: string | null) {
  const getToken = useAccessToken();
  const qs = targetOperatorId ? `?operatorId=${encodeURIComponent(targetOperatorId)}` : '';
  return {
    queryKey: ['roles', targetOperatorId ?? 'all'] as const,
    listFn: () => apiFetch<RoleSummary[]>(`/api/v1/roles${qs}`, {}, getToken),
  };
}

export function useRolePermissions(roleId: string | null) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['roles', 'permissions', roleId],
    queryFn: () => apiFetch<RolePermissionRow[]>(`/api/v1/roles/${roleId}/permissions`, {}, getToken),
    enabled: !!roleId,
  });
}

export type CloneRoleBody = {
  systemRoleCode: string;
  operatorId?: string | null;
  newCode: string;
  newDisplayName: string;
};

export function useCloneRole() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CloneRoleBody) =>
      apiFetch<{ role_id: string }>('/api/v1/roles', {
        method: 'POST',
        body: JSON.stringify({
          SystemRoleCode: body.systemRoleCode,
          OperatorId:     body.operatorId ?? null,
          NewCode:        body.newCode,
          NewDisplayName: body.newDisplayName,
        }),
      }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roles'] }),
  });
}

export function useSetRolePermissions(roleId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (permissionIds: string[]) =>
      apiFetch<void>(`/api/v1/roles/${roleId}/permissions`, {
        method: 'PUT',
        body: JSON.stringify({ PermissionIds: permissionIds }),
      }, getToken),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles'] });
      qc.invalidateQueries({ queryKey: ['roles', 'permissions', roleId] });
    },
  });
}

export function useDisableRole(roleId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<void>(`/api/v1/roles/${roleId}/disable`, { method: 'POST' }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roles'] }),
  });
}

// #160 — DELETE /api/v1/roles/{id}. Refused by the server when the role
// is SystemMaster or still assigned to one or more users. Errors surface
// via mutation.error so the caller can render them.
export function useDeleteRole(roleId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<void>(`/api/v1/roles/${roleId}`, { method: 'DELETE' }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roles'] }),
  });
}

export function useSetRoleAutoClone() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, enabled }: { roleId: string; enabled: boolean }) =>
      apiFetch<void>(`/api/v1/roles/${roleId}/auto-clone`, {
        method: 'PUT',
        body: JSON.stringify({ Enabled: enabled }),
      }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roles'] }),
  });
}

export function usePermissionsList() {
  const getToken = useAccessToken();
  return {
    queryKey: ['permissions'] as const,
    listFn: () => apiFetch<Permission[]>('/api/v1/permissions', {}, getToken),
  };
}

export type CreateOperatorBody = {
  code: string;
  displayName: string;
  legalName: string;
  country?: string | null;
  stateProvince?: string | null;
  timeZone?: string | null;
  dedicated?: boolean;
};

export function useCreateOperator() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateOperatorBody) =>
      apiFetch<{ operator_id: string }>('/api/v1/operators', {
        method: 'POST',
        body: JSON.stringify({
          Code:          body.code,
          DisplayName:   body.displayName,
          LegalName:     body.legalName,
          Country:       body.country ?? null,
          StateProvince: body.stateProvince ?? null,
          TimeZone:      body.timeZone ?? null,
          Dedicated:     body.dedicated ?? false,
        }),
      }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['operators'] }),
  });
}

export function useEditOperator(operatorId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Omit<CreateOperatorBody, 'code'>>) =>
      apiFetch<void>(`/api/v1/operators/${operatorId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          DisplayName:   body.displayName ?? null,
          LegalName:     body.legalName ?? null,
          Country:       body.country ?? null,
          StateProvince: body.stateProvince ?? null,
          TimeZone:      body.timeZone ?? null,
          Dedicated:     body.dedicated ?? null,
        }),
      }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['operators'] }),
  });
}

export function useOperatorAction(operatorId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (action: 'suspend' | 'restore') =>
      apiFetch<void>(`/api/v1/operators/${operatorId}/${action}`, { method: 'POST' }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['operators'] }),
  });
}

export function useAuthorizationsList(state?: string) {
  const getToken = useAccessToken();
  const params = state ? `?state=${encodeURIComponent(state)}` : '';
  return {
    queryKey: ['authorizations', state ?? 'all'] as const,
    listFn: () => apiFetch<AuthorizationListRow[]>(`/api/v1/authorizations${params}`, {}, getToken),
  };
}

export function useAuthorizationStatus(requestId: string | null) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['authorizations', 'status', requestId],
    queryFn: () => apiFetch<AuthorizationStatus>(`/api/v1/authorizations/${requestId}`, {}, getToken),
    enabled: !!requestId,
    refetchInterval: 30_000, // status changes when approvers act elsewhere
  });
}

export function useApproveAuthorization(requestId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { mfaFactor: string; notes?: string | null }) =>
      apiFetch<AuthorizationStatus>(`/api/v1/authorizations/${requestId}/approve`, {
        method: 'POST',
        body: JSON.stringify({ MfaFactor: body.mfaFactor, Notes: body.notes ?? null }),
      }, getToken),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['authorizations'] });
    },
  });
}

export function useRejectAuthorization(requestId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { notes?: string | null }) =>
      apiFetch<void>(`/api/v1/authorizations/${requestId}/reject`, {
        method: 'POST',
        body: JSON.stringify({ Notes: body.notes ?? null }),
      }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['authorizations'] }),
  });
}

export function useCancelAuthorization(requestId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<void>(`/api/v1/authorizations/${requestId}/cancel`, { method: 'POST' }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['authorizations'] }),
  });
}

/** Owner rule 2026-08-23: a DISABLED site never appears in a selection
 *  list anywhere in the RM — grids and label lookups still see it. */
export function selectableSites<T extends { status: string }>(rows: T[] | undefined): T[] {
  return (rows ?? []).filter((s) => s.status === 'active');
}

export function useSitesList(operatorId?: string | null) {
  const getToken = useAccessToken();
  const params = operatorId ? `?operatorId=${operatorId}` : '';
  return {
    queryKey: ['sites', operatorId ?? null] as const,
    listFn: () => apiFetch<SiteSummary[]>(`/api/v1/sites${params}`, {}, getToken),
  };
}

export function useSiteDetail(siteId: string | null) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['sites', 'detail', siteId],
    queryFn: () => apiFetch<SiteDetail>(`/api/v1/sites/${siteId}`, {}, getToken),
    enabled: !!siteId,
  });
}

export type CreateSiteBody = {
  operatorId?: string | null;
  code: string;
  displayName: string;
  addressLine1: string;
  addressLine2?: string | null;
  city: string;
  stateProvince?: string | null;
  postalCode?: string | null;
  timeZone?: string | null;
  businessHoursJson?: string | null;
  siteOwnerUserId?: string | null;
  /** Minutes after local midnight the site's gaming day starts (0-1439). Defaults to 0 server-side when omitted. */
  gamingDayOffsetMinutes?: number | null;
};

export function useCreateSite() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSiteBody) =>
      apiFetch<{ site_id: string }>('/api/v1/sites', {
        method: 'POST',
        body: JSON.stringify({
          OperatorId:        body.operatorId ?? null,
          Code:              body.code,
          DisplayName:       body.displayName,
          AddressLine1:      body.addressLine1,
          AddressLine2:      body.addressLine2 ?? null,
          City:              body.city,
          StateProvince:     body.stateProvince ?? null,
          PostalCode:        body.postalCode ?? null,
          TimeZone:          body.timeZone ?? null,
          BusinessHoursJson: body.businessHoursJson ?? null,
          SiteOwnerUserId:   body.siteOwnerUserId ?? null,
          GamingDayOffsetMinutes: body.gamingDayOffsetMinutes ?? null,
        }),
      }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sites'] }),
  });
}

export type EditSiteBody = {
  displayName?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  stateProvince?: string | null;
  postalCode?: string | null;
  timeZone?: string | null;
  businessHoursJson?: string | null;
  siteOwnerUserId?: string | null;
  releaseChannel?: 'stable' | 'beta' | null;
  /** Minutes after local midnight the site's gaming day starts (0-1439). */
  gamingDayOffsetMinutes?: number | null;
};

export function useEditSite(siteId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EditSiteBody) =>
      apiFetch<void>(`/api/v1/sites/${siteId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          DisplayName:       body.displayName ?? null,
          AddressLine1:      body.addressLine1 ?? null,
          AddressLine2:      body.addressLine2 ?? null,
          City:              body.city ?? null,
          StateProvince:     body.stateProvince ?? null,
          PostalCode:        body.postalCode ?? null,
          TimeZone:          body.timeZone ?? null,
          BusinessHoursJson: body.businessHoursJson ?? null,
          SiteOwnerUserId:   body.siteOwnerUserId ?? null,
          ReleaseChannel:    body.releaseChannel ?? null,
          GamingDayOffsetMinutes: body.gamingDayOffsetMinutes ?? null,
        }),
      }, getToken),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sites'] });
      qc.invalidateQueries({ queryKey: ['sites', 'detail', siteId] });
    },
  });
}

export function useSiteAction(siteId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (action: 'disable' | 'reactivate') =>
      apiFetch<void>(`/api/v1/sites/${siteId}/${action}`, { method: 'POST' }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sites'] }),
  });
}

// ---------- machines ----------

export type MachinesListFilter = {
  operatorId?: string | null;
  siteId?: string | null;
  ownershipKind?: string | null;
  status?: string | null;
};

export function useMachinesList(filter?: MachinesListFilter) {
  const getToken = useAccessToken();
  const q = new URLSearchParams();
  if (filter?.operatorId)    q.set('operatorId',    filter.operatorId);
  if (filter?.siteId)        q.set('siteId',        filter.siteId);
  if (filter?.ownershipKind) q.set('ownershipKind', filter.ownershipKind);
  if (filter?.status)        q.set('status',        filter.status);
  const qs = q.toString();
  return {
    queryKey: ['machines', filter ?? null] as const,
    listFn: () => apiFetch<MachineSummary[]>(
      `/api/v1/machines${qs ? `?${qs}` : ''}`, {}, getToken),
  };
}

export function useMachineDetail(machineId: string | null) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['machines', 'detail', machineId],
    queryFn: () => apiFetch<MachineDetail>(`/api/v1/machines/${machineId}`, {}, getToken),
    enabled: !!machineId,
  });
}

export type CreateMachineBody = {
  operatorId?: string | null;
  siteId: string;
  assetNumber: string;
  make: string;
  model: string;
  gameName?: string | null;
  protocol?: string | null;
  denominationCents: number;
  rtpBasisPoints?: number | null;
  ownershipKind?: 'operator' | 'supplier' | 'site_owner';
  ownerSupplierId?: string | null;
  ownerSiteOwnerUserId?: string | null;
  cashlessMode?: string | null;
  predecessorMachineId?: string | null;
};

export function useCreateMachine() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMachineBody) =>
      apiFetch<{ machine_id: string }>('/api/v1/machines', {
        method: 'POST',
        body: JSON.stringify({
          OperatorId:           body.operatorId ?? null,
          SiteId:               body.siteId,
          AssetNumber:          body.assetNumber,
          Make:                 body.make,
          Model:                body.model,
          GameName:             body.gameName ?? null,
          Protocol:             body.protocol ?? null,
          DenominationCents:    body.denominationCents,
          RtpBasisPoints:       body.rtpBasisPoints ?? null,
          OwnershipKind:        body.ownershipKind ?? 'operator',
          OwnerSupplierId:      body.ownerSupplierId ?? null,
          OwnerSiteOwnerUserId: body.ownerSiteOwnerUserId ?? null,
          CashlessMode:         body.cashlessMode ?? null,
          PredecessorMachineId: body.predecessorMachineId ?? null,
        }),
      }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['machines'] }),
  });
}

export type EditMachineBody = {
  siteId?: string | null;
  make?: string | null;
  model?: string | null;
  gameName?: string | null;
  protocol?: string | null;
  denominationCents?: number | null;
  rtpBasisPoints?: number | null;
  cashlessMode?: string | null;
};

export function useEditMachine(machineId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EditMachineBody) =>
      apiFetch<void>(`/api/v1/machines/${machineId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          SiteId:            body.siteId ?? null,
          Make:              body.make ?? null,
          Model:             body.model ?? null,
          GameName:          body.gameName ?? null,
          Protocol:          body.protocol ?? null,
          DenominationCents: body.denominationCents ?? null,
          RtpBasisPoints:    body.rtpBasisPoints ?? null,
          CashlessMode:      body.cashlessMode ?? null,
        }),
      }, getToken),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['machines'] });
      qc.invalidateQueries({ queryKey: ['machines', 'detail', machineId] });
    },
  });
}

export function useSetMachineOwnership(machineId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      kind: 'operator' | 'supplier' | 'site_owner';
      ownerSupplierId?: string | null;
      ownerSiteOwnerUserId?: string | null;
    }) =>
      apiFetch<void>(`/api/v1/machines/${machineId}/ownership`, {
        method: 'POST',
        body: JSON.stringify({
          Kind:                 body.kind,
          OwnerSupplierId:      body.ownerSupplierId ?? null,
          OwnerSiteOwnerUserId: body.ownerSiteOwnerUserId ?? null,
        }),
      }, getToken),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['machines'] });
      qc.invalidateQueries({ queryKey: ['machines', 'detail', machineId] });
    },
  });
}

export function useDecommissionMachine(machineId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string | null) =>
      apiFetch<void>(`/api/v1/machines/${machineId}/decommission`, {
        method: 'POST',
        body: JSON.stringify({ Reason: reason }),
      }, getToken),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['machines'] });
      qc.invalidateQueries({ queryKey: ['machines', 'detail', machineId] });
    },
  });
}

export function useReactivateMachine(machineId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<void>(`/api/v1/machines/${machineId}/reactivate`, { method: 'POST' }, getToken),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['machines'] });
      qc.invalidateQueries({ queryKey: ['machines', 'detail', machineId] });
    },
  });
}

export function useMachineMaintenance(machineId: string | null) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['machines', 'maintenance', machineId],
    queryFn: () => apiFetch<MachineMaintenanceEntry[]>(
      `/api/v1/machines/${machineId}/maintenance`, {}, getToken),
    enabled: !!machineId,
  });
}

/**
 * The last reported SC→RM meter packet for a machine. 404 (no meters yet) is a
 * normal state the caller renders as "not reported yet" — so don't retry it.
 */
export function useMachineLatestMeters(machineId: string | null) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['machines', 'meters', 'latest', machineId],
    queryFn: () => apiFetch<MachineMeterSnapshot>(
      `/api/v1/machines/${machineId}/meters/latest`, {}, getToken),
    enabled: !!machineId,
    retry: false,
  });
}

/**
 * Game-version history for a machine (financial meters spec §6.1/§9.4).
 * A machine with no meters-engine activity yet has no version rows at all —
 * the API reports that as 404 `no_versions` rather than an empty 200, so
 * (unlike most list endpoints) we treat that specific 404 as the normal
 * empty state and resolve `[]` instead of surfacing a query error.
 */
/** SAS machine events for one machine over a gaming-day range (machines.view). */
export function useMachineEvents(machineId: string | null, from: string, to: string) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['machines', 'events', machineId, from, to],
    queryFn: async () => {
      try {
        return await apiFetch<MachineEventRow[]>(
          `/api/v1/machines/${machineId}/events?from=${from}&to=${to}`, {}, getToken);
      } catch (e) {
        if ((e as ApiError).status === 404) return [];
        throw e;
      }
    },
    enabled: !!machineId && !!from && !!to,
  });
}

/** Mark a raised (issue/alarm) machine event reviewed (meters.audit-ack). */
export function useReviewMachineEvent() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) =>
      apiFetch<{ reviewed: boolean }>(`/api/v1/machines/events/${eventId}/review`, { method: 'POST' }, getToken),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['machines', 'events'] }),
  });
}

/** Open collection/maintenance mode for a site (optionally one machine). */
export function useOpenAccessWindow() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ siteId, reason, machineId, minutes }: {
      siteId: string; reason: string; machineId?: string | null; minutes?: number;
    }) =>
      apiFetch<AccessWindowOpenResult>(`/api/v1/sites/${siteId}/access-windows`, {
        method: 'POST',
        body: JSON.stringify({ reason, machineId: machineId ?? null, minutes: minutes ?? 30 }),
      }, getToken),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['machines'] }),
  });
}

export function useCloseAccessWindow() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (windowId: string) =>
      apiFetch<{ id: string; closed: boolean }>(`/api/v1/access-windows/${windowId}/close`, { method: 'POST' }, getToken),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['machines'] }),
  });
}

export function useOpenAccessWindows(siteId: string | null) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['machines', 'access-windows', siteId],
    queryFn: () => apiFetch<OpenAccessWindow[]>(`/api/v1/sites/${siteId}/access-windows/open`, {}, getToken),
    enabled: !!siteId,
  });
}

/** Meter quarantine review state + the packets around the event (machines.view). */
export function useMachineMeterQuarantine(machineId: string | null, enabled = true) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['machines', 'meter-quarantine', machineId],
    queryFn: () => apiFetch<MachineMeterQuarantine>(
      `/api/v1/machines/${machineId}/meters/quarantine`, {}, getToken),
    enabled: !!machineId && enabled,
  });
}

/**
 * Resolve a meter quarantine (machines.admin + ARMS-only, enforced server-side).
 * resolution: 'ram_reset' replays the raw history so EVERY reset in the
 * unresolved window (which may span days) mints its own zero-baseline machine
 * version; 'missed_wrap' books the move as a wrap; 'machine_fault' re-baselines
 * at the latest reading. Invalidates the machine + revenue query trees — day
 * rows and dashboard totals both change.
 */
export function useResolveMeterQuarantine() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ machineId, resolution }: { machineId: string; resolution: 'ram_reset' | 'missed_wrap' | 'machine_fault' }) =>
      apiFetch<ResolveMeterQuarantineResult>(`/api/v1/machines/${machineId}/meters/quarantine/resolve`, {
        method: 'POST',
        body: JSON.stringify({ resolution }),
      }, getToken),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['machines'] });
      void qc.invalidateQueries({ queryKey: ['revenue'] });
    },
  });
}

export function useMachineVersions(machineId: string | null) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['machines', 'versions', machineId],
    queryFn: async () => {
      try {
        return await apiFetch<MachineVersion[]>(`/api/v1/machines/${machineId}/versions`, {}, getToken);
      } catch (e) {
        if ((e as ApiError).status === 404) return [];
        throw e;
      }
    },
    enabled: !!machineId,
  });
}

export function useMachineDailyMeters(machineId: string | null, from: string, to: string) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['machines', 'meters', 'daily', machineId, from, to],
    queryFn: async () => {
      try {
        return await apiFetch<DailyMeterRow[]>(
          `/api/v1/machines/${machineId}/meters/daily?from=${from}&to=${to}`, {}, getToken);
      } catch (e) {
        if ((e as ApiError).status === 404) return [];
        throw e;
      }
    },
    enabled: !!machineId && !!from && !!to,
  });
}

export type EditMachineVersionBody = {
  gameName?: string | null;
  rtpBasisPoints?: number | null;
  denominationCents?: number | null;
  confirmReview?: boolean;
};

export function useEditMachineVersion(machineId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ versionId, ...body }: { versionId: string } & EditMachineVersionBody) =>
      apiFetch<void>(`/api/v1/machines/${machineId}/versions/${versionId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          GameName:          body.gameName ?? null,
          RtpBasisPoints:    body.rtpBasisPoints ?? null,
          DenominationCents: body.denominationCents ?? null,
          ConfirmReview:     body.confirmReview ?? false,
        }),
      }, getToken),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['machines', 'versions', machineId] });
      qc.invalidateQueries({ queryKey: ['machines'] });
    },
  });
}

export function useAppendMachineMaintenance(machineId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      performedAt?: string | null;
      technicianUserId: string;
      action: string;
      notes?: string | null;
      partsReplacedJson?: string | null;
    }) =>
      apiFetch<{ entry_id: string }>(`/api/v1/machines/${machineId}/maintenance`, {
        method: 'POST',
        body: JSON.stringify({
          PerformedAt:       body.performedAt ?? null,
          TechnicianUserId:  body.technicianUserId,
          Action:            body.action,
          Notes:             body.notes ?? null,
          PartsReplacedJson: body.partsReplacedJson ?? null,
        }),
      }, getToken),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['machines', 'maintenance', machineId] }),
  });
}

// ---------- suppliers (ACES-tier) ----------

export function useSuppliersList() {
  const getToken = useAccessToken();
  return {
    queryKey: ['suppliers'] as const,
    listFn: () => apiFetch<SupplierSummary[]>('/api/v1/suppliers', {}, getToken),
  };
}

export function useSupplierDetail(supplierId: string | null) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['suppliers', 'detail', supplierId],
    queryFn: () => apiFetch<SupplierDetail>(`/api/v1/suppliers/${supplierId}`, {}, getToken),
    enabled: !!supplierId,
  });
}

export type CreateSupplierBody = {
  code: string;
  displayName: string;
  description?: string | null;
  primaryContactEmail: string;
};

export function useCreateSupplier() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSupplierBody) =>
      apiFetch<{ supplier_id: string }>('/api/v1/suppliers', {
        method: 'POST',
        body: JSON.stringify({
          Code:                body.code,
          DisplayName:         body.displayName,
          Description:         body.description ?? null,
          PrimaryContactEmail: body.primaryContactEmail,
        }),
      }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suppliers'] }),
  });
}

export function useEditSupplier(supplierId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Omit<CreateSupplierBody, 'code'>>) =>
      apiFetch<void>(`/api/v1/suppliers/${supplierId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          DisplayName:         body.displayName ?? null,
          Description:         body.description ?? null,
          PrimaryContactEmail: body.primaryContactEmail ?? null,
        }),
      }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suppliers'] }),
  });
}

export function useSupplierAction(supplierId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (action: 'disable' | 'reactivate') =>
      apiFetch<void>(`/api/v1/suppliers/${supplierId}/${action}`, { method: 'POST' }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suppliers'] }),
  });
}

// ---------- system settings ----------

export function useSettingDefinitions() {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['settings', 'definitions'],
    queryFn: () => apiFetch<SettingDefinition[]>('/api/v1/settings/definitions', {}, getToken),
    staleTime: 5 * 60_000,
  });
}

export function useEffectiveSettings(category?: string | null) {
  const getToken = useAccessToken();
  const params = category ? `?category=${encodeURIComponent(category)}` : '';
  return useQuery({
    queryKey: ['settings', 'effective', category ?? 'all'],
    queryFn: () => apiFetch<EffectiveSetting[]>(`/api/v1/settings/effective${params}`, {}, getToken),
  });
}

export function useSetOperatorSetting() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value, operatorId }: { key: string; value: string; operatorId?: string | null }) =>
      apiFetch<{ setting_id: string; value: string }>(`/api/v1/settings/operator/${encodeURIComponent(key)}`, {
        method: 'PUT',
        body: JSON.stringify({ Value: value, OperatorId: operatorId ?? null }),
      }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'effective'] }),
  });
}

export function useClearOperatorSetting() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, operatorId }: { key: string; operatorId?: string | null }) => {
      const params = operatorId ? `?operatorId=${operatorId}` : '';
      return apiFetch<void>(`/api/v1/settings/operator/${encodeURIComponent(key)}${params}`, {
        method: 'DELETE',
      }, getToken);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'effective'] }),
  });
}

export function useSetAcesSetting() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      apiFetch<{ setting_id: string; value: string }>(`/api/v1/settings/aces/${encodeURIComponent(key)}`, {
        method: 'PUT',
        body: JSON.stringify({ Value: value }),
      }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'effective'] }),
  });
}

export function useClearAcesSetting() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key }: { key: string }) =>
      apiFetch<void>(`/api/v1/settings/aces/${encodeURIComponent(key)}`, { method: 'DELETE' }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'effective'] }),
  });
}

export function useUpdateOperatorBranding(operatorId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { primaryHex?: string | null; accentHex?: string | null }) =>
      apiFetch<void>(`/api/v1/operators/${operatorId}/branding`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['operators', operatorId, 'branding'] }),
  });
}

// ----- System-user public keys (Service Auth methodology, S2) -----

export type UserPubKeyDto = {
  id: string;
  algorithm: string;
  displayName: string;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
};

export function useUserPubKeys(userId: string) {
  const getToken = useAccessToken();
  return {
    queryKey: ['user-pubkeys', userId] as const,
    listFn: () => apiFetch<UserPubKeyDto[]>(
      `/api/v1/users/${userId}/pubkeys`, {}, getToken),
  };
}

export function useAddUserPubKey() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, publicKeyBase64Url, displayName, revokeExisting }:
                 { userId: string; publicKeyBase64Url: string;
                   displayName?: string; revokeExisting?: boolean }) =>
      apiFetch<UserPubKeyDto>(
        `/api/v1/users/${userId}/pubkeys`, {
          method: 'POST',
          body: JSON.stringify({
            publicKeyBase64Url,
            displayName: displayName ?? null,
            revokeExisting: revokeExisting ?? false,
          }),
        }, getToken),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: ['user-pubkeys', vars.userId] }),
  });
}

export function useRevokeUserPubKey() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, keyId }: { userId: string; keyId: string }) =>
      apiFetch<{ id: string; revokedAt: string }>(
        `/api/v1/users/${userId}/pubkeys/${keyId}`,
        { method: 'DELETE' }, getToken),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: ['user-pubkeys', vars.userId] }),
  });
}

// ----- Software releases (Stage-2 commissioning M6) -----

export function useSoftwareReleasesList(channel?: string | null) {
  const getToken = useAccessToken();
  const params = channel ? `?channel=${channel}` : '';
  return {
    queryKey: ['software-releases', channel ?? null] as const,
    listFn: () => apiFetch<SoftwareReleaseSummary[]>(
      `/api/v1/provisioning/software-releases${params}`, {}, getToken),
  };
}

export type CreateSoftwareReleaseBody = {
  channel: string;
  version: string;
  containerImage: string;
  imageDigestSha256: string;
  cosignSignature: string;
  minimumBootstrapVersion?: string | null;
  upgradeClass?: string | null;
  notes?: string | null;
};

export function useCreateSoftwareRelease() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSoftwareReleaseBody) =>
      apiFetch<{ release_id: string; channel: string; version: string }>(
        '/api/v1/provisioning/software-releases', {
          method: 'POST',
          body: JSON.stringify({
            Channel:                  body.channel,
            Version:                  body.version,
            ContainerImage:           body.containerImage,
            ImageDigestSha256:        body.imageDigestSha256,
            CosignSignature:          body.cosignSignature,
            MinimumBootstrapVersion:  body.minimumBootstrapVersion ?? null,
            UpgradeClass:             body.upgradeClass ?? 'planned',
            Notes:                    body.notes ?? null,
          }),
        }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['software-releases'] }),
  });
}

// ----- R2 catalogue (multi-target bundle releases) -----
//
// Sister surface to the legacy single-component software-releases above.
// Backed by /api/v1/releases/* and /api/v1/channel-heads/*. The legacy
// SC-container catalogue keeps working unchanged; new artefact classes
// (XU4 OS, Mac tool, ESP32 firmware, MachineGateway) come through here.

export type ReleaseCatalogueEntry = {
  id: string;
  target: string;          // 'xu4' | 'cm5' | 'esp32p4' | 'mac-tool' | 'sc-container' …
  version: string;
  displayName: string;
  state: string;           // 'draft' | 'in_test' | 'released' | 'deprecated'
  channel: string;
  manifestJson: string | null;
  releaseNotesMd: string | null;
  createdAt: string;
  releasedAt: string | null;
  deprecatedAt: string | null;
  createdByUserId: string | null;
  createdByName: string | null;
  createdBySystem: boolean;
  artefacts: Array<{
    role: string;
    sha256: string;
    sizeBytes: number;
    contentType: string;
    originalFileName: string | null;
    hasSignature: boolean;
    hasManifest: boolean;
  }>;
};

export type ChannelHead = {
  target: string;
  channel: string;
  currentReleaseId: string | null;
  previousReleaseId: string | null;
  updatedAt: string;
};

export function useReleaseCatalogue(target?: string | null, state?: string | null) {
  const getToken = useAccessToken();
  const params = new URLSearchParams();
  if (target) params.set('target', target);
  if (state) params.set('state', state);
  const qs = params.toString() ? `?${params}` : '';
  return {
    queryKey: ['releases', target ?? null, state ?? null] as const,
    listFn: () => apiFetch<ReleaseCatalogueEntry[]>(
      `/api/v1/releases${qs}`, {}, getToken),
  };
}

export function useChannelHeads(target?: string | null) {
  const getToken = useAccessToken();
  const params = target ? `?target=${target}` : '';
  return {
    queryKey: ['channel-heads', target ?? null] as const,
    listFn: () => apiFetch<ChannelHead[]>(
      `/api/v1/channel-heads${params}`, {}, getToken),
  };
}

export type ApiVersion = {
  service: string;
  version: string;
  sha: string;
  buildTime: string;
  runtime: string;
};

// /api/v1/version is anonymous so a logged-out user still sees the chip.
// Mark stale after 5 min so a redeploy lands within a single page reload.
export function useApiVersion() {
  return useQuery({
    queryKey: ['api-version'] as const,
    queryFn: () => apiFetch<ApiVersion>('/api/v1/version'),
    staleTime: 5 * 60_000,
    retry: 0,
  });
}

export function useMintRelease() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      target: string; version: string; displayName?: string;
      channel?: string; releaseNotesMd?: string;
    }) => apiFetch<{ id: string; target: string; version: string; state: string }>(
      '/api/v1/releases', {
        method: 'POST',
        body: JSON.stringify(body),
      }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['releases'] }),
  });
}

// Uploads the raw artefact bytes. The server hashes them and stores the
// blob + SHA-256 in postgres bytea. Content-Type is set explicitly so
// apiFetch doesn't auto-pick application/json.
export function useUploadArtefact() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ releaseId, role, file }:
                       { releaseId: string; role: string; file: File }) => {
      const buf = await file.arrayBuffer();
      return apiFetch<{ role: string; sha256: string; size_bytes: number }>(
        `/api/v1/releases/${releaseId}/artefacts/${role}`, {
          method: 'POST',
          body: buf,
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
            'X-Original-Filename': file.name,
          },
        }, getToken);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['releases'] }),
  });
}

// Downloads an artefact's bytes to the user's local drive. The server's
// /download endpoint 302-redirects to a presigned blob URL for the Site
// Controller, which a browser fetch can't read cross-origin — so we pass
// ?proxy=true and the API streams the bytes through itself (same origin,
// auth-checked). apiFetchBlob carries the bearer token; we wrap the Blob in
// an object URL and click a synthetic <a download> to save it, then revoke.
export function useDownloadArtefact() {
  const getToken = useAccessToken();
  return useMutation({
    mutationFn: async ({ releaseId, role, fileName }:
                       { releaseId: string; role: string; fileName: string | null }) => {
      const blob = await apiFetchBlob(
        `/api/v1/releases/${releaseId}/artefacts/${role}/download?proxy=true`,
        getToken,
      );
      const url = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName ?? `${role}.bin`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        // Defer revoke so the browser has started the download.
        setTimeout(() => URL.revokeObjectURL(url), 10_000);
      }
      return { role, fileName };
    },
  });
}

export function useTransitionReleaseState() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, state, deprecationReason }:
                 { id: string; state: string; deprecationReason?: string }) =>
      apiFetch<{ id: string; state: string }>(
        `/api/v1/releases/${id}/state`, {
          method: 'PUT',
          body: JSON.stringify({ state, deprecationReason: deprecationReason ?? null }),
        }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['releases'] }),
  });
}

export type DeleteReleaseResult = {
  id: string;
  target: string;
  version: string;
  artefactCount: number;
  reclaimedBytes: number;
};

export function useDeleteRelease() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      apiFetch<DeleteReleaseResult>(
        `/api/v1/releases/${id}`, {
          method: 'DELETE',
          body: JSON.stringify({ reason: reason ?? null }),
        }, getToken),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['releases'] });
      qc.invalidateQueries({ queryKey: ['channel-heads'] });
    },
  });
}

export function useRepointChannelHead() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ target, channel, releaseId, reason,
                   rolloutBatchSize, rolloutBatchIntervalSeconds }:
                 { target: string; channel: string; releaseId: string; reason?: string;
                   /** PR 3 — opt-in staged rollout pacing. */
                   rolloutBatchSize?: number | null;
                   rolloutBatchIntervalSeconds?: number | null }) =>
      apiFetch<ChannelHead>(
        `/api/v1/channel-heads/${target}/${channel}`, {
          method: 'PUT',
          body: JSON.stringify({
            releaseId,
            reason: reason ?? null,
            rolloutBatchSize: rolloutBatchSize ?? null,
            rolloutBatchIntervalSeconds: rolloutBatchIntervalSeconds ?? null,
          }),
        }, getToken),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['channel-heads'] });
      qc.invalidateQueries({ queryKey: ['release-rollouts'] });
    },
  });
}

// PR 3 — release rollout runs.
export type ReleaseRolloutRun = {
  id: string;
  target: string;
  channel: string;
  releaseId: string;
  previousReleaseId: string | null;
  totalEligibleScs: number;
  batchSize: number;
  batchIntervalSeconds: number;
  status: 'in_progress' | 'paused' | 'done' | 'cancelled';
  startedAt: string;
  pausedAt: string | null;
  resumedAt: string | null;
  cancelledAt: string | null;
  doneAt: string | null;
  cancelledReason: string | null;
};

export function useReleaseRollouts(statusFilter?: 'in_progress' | 'paused' | 'done' | 'cancelled') {
  const getToken = useAccessToken();
  const params = statusFilter ? `?status=${statusFilter}` : '';
  return useQuery({
    queryKey: ['release-rollouts', statusFilter ?? null] as const,
    queryFn: () => apiFetch<ReleaseRolloutRun[]>(`/api/v1/release-rollout-runs${params}`, {}, getToken),
    refetchInterval: 15_000,
  });
}

export function useRolloutAction() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action, reason }:
                 { id: string; action: 'pause' | 'resume' | 'cancel'; reason?: string }) =>
      apiFetch<{ id: string; status: string }>(
        `/api/v1/release-rollout-runs/${id}/${action}`, {
          method: 'POST',
          body: action === 'cancel'
            ? JSON.stringify({ reason: reason ?? null })
            : undefined,
        }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['release-rollouts'] }),
  });
}

export function useRollbackChannelHead() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ target, channel }: { target: string; channel: string }) =>
      apiFetch<ChannelHead>(
        `/api/v1/channel-heads/${target}/${channel}/rollback`,
        { method: 'POST' }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channel-heads'] }),
  });
}

export function usePinSoftwareRelease() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (releaseId: string) =>
      apiFetch<{ release_id: string; channel: string; version: string }>(
        `/api/v1/provisioning/software-releases/${releaseId}/pin`,
        { method: 'POST' }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['software-releases'] }),
  });
}

// ----- IoT QR batches + machine devices (ESP32 Deployment Plan M1) -----

export function useIotQrBatchesList() {
  const getToken = useAccessToken();
  return {
    queryKey: ['iot-qr-batches'] as const,
    listFn: () => apiFetch<IotQrBatchSummary[]>('/api/v1/factory/iot-qr-batches', {}, getToken),
  };
}

export type GenerateIotQrBatchBody = {
  code: string;
  notes?: string | null;
  count: number;
  labelPaper?: string | null;
  labelWidthMm?: number | null;
  labelHeightMm?: number | null;
  colsPerSheet?: number | null;
  rowsPerSheet?: number | null;
};

export function useGenerateIotQrBatch() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GenerateIotQrBatchBody) =>
      apiFetch<{ batch_id: string; code: string; count: number }>(
        '/api/v1/factory/iot-qr-batches',
        {
          method: 'POST',
          body: JSON.stringify({
            Code:           body.code,
            Notes:          body.notes ?? null,
            Count:          body.count,
            LabelPaper:     body.labelPaper     ?? null,
            LabelWidthMm:   body.labelWidthMm   ?? null,
            LabelHeightMm:  body.labelHeightMm  ?? null,
            ColsPerSheet:   body.colsPerSheet   ?? null,
            RowsPerSheet:   body.rowsPerSheet   ?? null,
          }),
        }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['iot-qr-batches'] }),
  });
}

export function useIotQrBatchDetail(batchId: string | null) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['iot-qr-batches', 'detail', batchId],
    queryFn:  () => apiFetch<IotQrBatchSummary>(`/api/v1/factory/iot-qr-batches/${batchId}`, {}, getToken),
    enabled:  !!batchId,
  });
}

export function useApplyIotQrBatchHeader() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ batchId, templateId }: { batchId: string; templateId: string }) =>
      apiFetch<void>(`/api/v1/factory/iot-qr-batches/${batchId}/header-template`, {
        method: 'PUT',
        body: JSON.stringify({ TemplateId: templateId }),
      }, getToken),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['iot-qr-batches'] });
      qc.invalidateQueries({ queryKey: ['iot-qr-batches', 'detail', vars.batchId] });
    },
  });
}

export function useClearIotQrBatchHeader() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (batchId: string) =>
      apiFetch<void>(`/api/v1/factory/iot-qr-batches/${batchId}/header-template`, {
        method: 'DELETE',
      }, getToken),
    onSuccess: (_d, batchId) => {
      qc.invalidateQueries({ queryKey: ['iot-qr-batches'] });
      qc.invalidateQueries({ queryKey: ['iot-qr-batches', 'detail', batchId] });
    },
  });
}

export function useIotQrBatchPairs(batchId: string | null) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['iot-qr-batches', 'pairs', batchId],
    queryFn:  () => apiFetch<IotQrPairSummary[]>(`/api/v1/factory/iot-qr-batches/${batchId}/qr-pairs`, {}, getToken),
    enabled:  !!batchId,
  });
}

/**
 * Build the URL for an IoT QR batch print PDF. Mirrors scQrBatchPrintUrl
 * but targets the IoT factory endpoint — HAT stickers are single labels
 * (no DEVICE+PACKAGING split) so the PDF carries one QR per pair.
 * Margins + gutter mirror the SC params 1:1.
 */
export function iotQrBatchPrintUrl(batchId: string, opts: {
  paper: string; widthMm: number; heightMm: number;
  cols: number; rows: number;
  marginLeftMm: number; marginRightMm: number;
  marginTopMm: number; marginBottomMm: number; gutterMm: number;
}): string {
  const q = new URLSearchParams({
    paper:           opts.paper,
    widthMm:         String(opts.widthMm),
    heightMm:        String(opts.heightMm),
    cols:            String(opts.cols),
    rows:            String(opts.rows),
    marginLeftMm:    String(opts.marginLeftMm),
    marginRightMm:   String(opts.marginRightMm),
    marginTopMm:     String(opts.marginTopMm),
    marginBottomMm:  String(opts.marginBottomMm),
    gutterMm:        String(opts.gutterMm),
  });
  return `/api/v1/factory/iot-qr-batches/${batchId}/print?${q.toString()}`;
}

export function useMachineDevicesList(opts?: { status?: string; siteControllerId?: string }) {
  const getToken = useAccessToken();
  const q = new URLSearchParams();
  if (opts?.status) q.set('status', opts.status);
  if (opts?.siteControllerId) q.set('siteControllerId', opts.siteControllerId);
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return {
    queryKey: ['machine-devices', opts?.status ?? null, opts?.siteControllerId ?? null] as const,
    listFn: () => apiFetch<MachineDeviceSummary[]>(`/api/v1/provisioning/machine-devices${suffix}`, {}, getToken),
  };
}

// ----- ESP32 firmware OTA (M8) — status + skew + rollout pause -----

/** esp32-firmware is the only rollout target the pause control governs. */
export const FIRMWARE_ROLLOUT_TARGET = 'esp32-firmware';

/**
 * Per-HAT running firmware (version + sha + reported-at) as last reported by
 * the SC. Gated server-side by provisioning.machine-devices.view; `enabled`
 * lets callers defer the fetch until a detail surface is opened so we never
 * issue a 403. GET /api/v1/provisioning/machine-devices/{id}/firmware.
 */
export function useMachineDeviceFirmware(id: string | null, enabled: boolean = true) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['machine-device-firmware', id] as const,
    queryFn: () => apiFetch<MachineDeviceFirmware>(
      `/api/v1/provisioning/machine-devices/${id}/firmware`, {}, getToken),
    enabled: enabled && !!id,
    staleTime: 15_000,
  });
}

/**
 * Fleet firmware skew — one bucket per distinct running firmware version (plus
 * a null-version "never reported" bucket). provisioning.machine-devices.view.
 * GET /api/v1/provisioning/machine-devices/firmware-skew.
 */
export function useFirmwareSkew(enabled: boolean = true) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['firmware-skew'] as const,
    queryFn: () => apiFetch<FirmwareSkew>(
      '/api/v1/provisioning/machine-devices/firmware-skew', {}, getToken),
    enabled,
    staleTime: 15_000,
  });
}

/**
 * Pause / resume the esp32-firmware OTA rollout. ARMS-Staff only
 * (provisioning.releases.promote). PUT
 * /api/v1/provisioning/machine-devices/firmware-rollout/{target}/pause.
 */
export function useSetFirmwareRolloutPaused() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ paused, target = FIRMWARE_ROLLOUT_TARGET }: { paused: boolean; target?: string }) =>
      apiFetch<FirmwareRolloutPauseState>(
        `/api/v1/provisioning/machine-devices/firmware-rollout/${target}/pause`,
        { method: 'PUT', body: JSON.stringify({ paused }) }, getToken),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['firmware-skew'] });
      qc.invalidateQueries({ queryKey: ['machine-devices'] });
    },
  });
}

// ----- Remote HAT Console (P0, Mode A) -----
//
// Client functions for the RMS session broker (Workstream 2). The console
// dialog (MachineDeviceConsoleDialog) is stateful and long-lived — it starts a
// session, polls frames with a live cursor, and MUST tear the session down on
// close/unmount — so these are plain async functions parameterised by getToken
// rather than useQuery/useMutation hooks. The dialog grabs getToken via
// useAccessToken() once and passes it down, then drives the poll itself with a
// TanStack useQuery keyed on the cursor.
//
// All error paths surface the server's HTTP status + `code` + `message` via the
// apiFetch ApiError shape (feedback_errors_visible_in_ui); the dialog renders
// them, never a native alert.

/** Permission gating the whole HAT-console surface (button + endpoints). */
export const HAT_CONSOLE_PERMISSION = 'provisioning.hat-console';

/** How often the portal polls for new frames (design ledger: portal 1 s). */
export const HAT_CONSOLE_POLL_MS = 1_000;

type GetToken = () => Promise<string | null>;

/**
 * POST /api/v1/machines/{machineDeviceId}/console/sessions — open a live console
 * session on a commissioned, online HAT whose firmware supports the console
 * task. On 200 returns `{ sessionId, framesUrl, modeBSupported, resumed, mode,
 * tookOver }` (console-session-contract.md).
 *
 * Behaviour matrix (server-side, summarised): no active session → fresh
 * session (`resumed: false`); the CALLER already holds one and `takeover` is
 * absent/false → the existing session is returned untouched (`resumed: true`,
 * `mode` = its current mode — the caller must not reset mode or re-send a
 * pending mode/filter call); ANOTHER user holds one and `takeover` is
 * absent/false → 409 `console_session_active` with `activeSessionId` /
 * `heldByUserId` / `heldByDisplayName` / `startedAt` on the error's
 * `details` (the caller renders the held-session panel + Take-over button);
 * passing `{ takeover: true }` ends whoever holds it (caller or another
 * user) and opens a fresh one (`resumed: false, tookOver: true`).
 *
 * Omit `opts` (or `takeover`) for a normal open/resume — this deliberately
 * sends no body then, exactly like the pre-resume contract, so callers/tests
 * that don't care about takeover keep seeing a plain 2-arg call.
 *
 * Throws an ApiError on 404 (not_found), 409 (not_commissioned | hat_offline |
 * console_unsupported_firmware | console_session_active), or 403 (no
 * permission). The caller renders `err.status` + `err.code` + `err.message`
 * (+ `err.details` for the held-session fields).
 */
export function startConsoleSession(
  machineDeviceId: string,
  getToken: GetToken,
  opts?: { takeover?: boolean },
): Promise<StartConsoleSessionResponse> {
  return apiFetch<StartConsoleSessionResponse>(
    `/api/v1/machines/${machineDeviceId}/console/sessions`,
    opts?.takeover
      ? { method: 'POST', body: JSON.stringify({ takeover: true }) }
      : { method: 'POST' },
    getToken,
  );
}

/**
 * GET /api/v1/console/sessions/{sessionId}/frames?sinceSeq= — poll for frames
 * after `sinceSeq`. Returns `{ frames, nextSeq, ended?, reason? }`; advance the
 * cursor to `nextSeq` on the next call. `ended: true` means the session closed
 * (stopped_by_user, dead-man, TTL, hat_offline, …) — stop polling and surface
 * `reason`.
 */
export function pollConsoleFrames(
  sessionId: string,
  sinceSeq: number,
  getToken: GetToken,
): Promise<ConsoleFramesResponse> {
  return apiFetch<ConsoleFramesResponse>(
    `/api/v1/console/sessions/${sessionId}/frames?sinceSeq=${sinceSeq}`,
    {},
    getToken,
  );
}

/**
 * DELETE /api/v1/console/sessions/{sessionId} — end the session (stopped_by_user).
 * Idempotent server-side. Called on explicit stop AND on dialog close/unmount so
 * no orphaned session or upstream traffic is left running (zero-leak invariant,
 * design §4). Returns the parsed body; callers usually ignore it.
 */
export function stopConsoleSession(
  sessionId: string,
  getToken: GetToken,
): Promise<{ sessionId: string; ended: boolean; reason: string }> {
  return apiFetch<{ sessionId: string; ended: boolean; reason: string }>(
    `/api/v1/console/sessions/${sessionId}`,
    { method: 'DELETE' },
    getToken,
  );
}

/**
 * POST /api/v1/console/sessions/{sessionId}/mode — switch a live session between
 * mode "A" (hat_diag lines, the default) and mode "B" (raw SAS-comms capture).
 * Returns `{ sessionId, mode }` on 200.
 *
 * Throws an ApiError (never swallowed — apiFetch throws on any non-2xx) on:
 *  - 400 `validation` — mode is not "A"/"B";
 *  - 404 `not_found` — session missing or out of tenancy scope;
 *  - 409 `session_ended` — the session already closed;
 *  - 409 `sas_capture_unsupported_firmware` — the server-side rail behind the
 *    portal's disabled toggle (the HAT's firmware predates mode-B support;
 *    the caller should already have gated on `modeBSupported` from the start
 *    response, so this is the belt-and-braces path if it's posted anyway);
 *  - 403 — no `provisioning.hat-console` permission.
 * The caller renders `err.status` + `err.code` + `err.message`.
 */
export function setConsoleMode(
  sessionId: string,
  mode: 'A' | 'B',
  getToken: GetToken,
): Promise<{ sessionId: string; mode: string }> {
  return apiFetch<{ sessionId: string; mode: string }>(
    `/api/v1/console/sessions/${sessionId}/mode`,
    { method: 'POST', body: JSON.stringify({ mode }) },
    getToken,
  );
}

/**
 * POST /api/v1/console/sessions/{sessionId}/capture-filter — toggle whether a
 * mode-B (raw SAS-comms capture) session includes routine general-poll traffic
 * (long-poll / short-poll requests with no notable response) in the stream, or
 * filters it out. Default (server + portal) is filtered — `showGeneralPolls:
 * false`. Returns `{ sessionId, showGeneralPolls }` on 200.
 *
 * Throws an ApiError (never swallowed) on:
 *  - 400 `validation` — showGeneralPolls is not a boolean;
 *  - 404 `not_found` — session missing or out of tenancy scope;
 *  - 409 `session_ended` — the session already closed;
 *  - 403 — no `provisioning.hat-console` permission.
 * The caller renders `err.status` + `err.code` + `err.message`.
 */
export function setCaptureFilter(
  sessionId: string,
  showGeneralPolls: boolean,
  getToken: GetToken,
): Promise<{ sessionId: string; showGeneralPolls: boolean }> {
  return apiFetch<{ sessionId: string; showGeneralPolls: boolean }>(
    `/api/v1/console/sessions/${sessionId}/capture-filter`,
    { method: 'POST', body: JSON.stringify({ showGeneralPolls }) },
    getToken,
  );
}

// ----- Machine (cabinet) QR batches (ESP32 Deployment Plan §3 M6) -----

export function useMachineQrBatchesList() {
  const getToken = useAccessToken();
  return {
    queryKey: ['machine-qr-batches'] as const,
    listFn: () => apiFetch<MachineQrBatchSummary[]>('/api/v1/factory/machine-qr-batches', {}, getToken),
  };
}

export type GenerateMachineQrBatchBody = {
  code: string;
  notes?: string | null;
  count: number;
  intendedOperatorId: string;
  labelPaper?: string | null;
  labelWidthMm?: number | null;
  labelHeightMm?: number | null;
  colsPerSheet?: number | null;
  rowsPerSheet?: number | null;
  headerTemplateId?: string | null;
};

export function useGenerateMachineQrBatch() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GenerateMachineQrBatchBody) =>
      apiFetch<{ batch_id: string; code: string; count: number }>(
        '/api/v1/factory/machine-qr-batches',
        {
          method: 'POST',
          body: JSON.stringify({
            Code:               body.code,
            Notes:              body.notes ?? null,
            Count:              body.count,
            IntendedOperatorId: body.intendedOperatorId,
            LabelPaper:         body.labelPaper      ?? null,
            LabelWidthMm:       body.labelWidthMm    ?? null,
            LabelHeightMm:      body.labelHeightMm   ?? null,
            ColsPerSheet:       body.colsPerSheet    ?? null,
            RowsPerSheet:       body.rowsPerSheet    ?? null,
            HeaderTemplateId:   body.headerTemplateId ?? null,
          }),
        }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['machine-qr-batches'] }),
  });
}

export function useMachineQrBatchDetail(batchId: string | null) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['machine-qr-batches', 'detail', batchId],
    queryFn:  () => apiFetch<MachineQrBatchSummary>(`/api/v1/factory/machine-qr-batches/${batchId}`, {}, getToken),
    enabled:  !!batchId,
  });
}

export function useMachineQrBatchPairs(batchId: string | null) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['machine-qr-batches', 'pairs', batchId],
    queryFn:  () => apiFetch<MachineQrPairSummary[]>(`/api/v1/factory/machine-qr-batches/${batchId}/qr-pairs`, {}, getToken),
    enabled:  !!batchId,
  });
}

export function machineQrBatchPrintUrl(batchId: string, opts: {
  paper: string; widthMm: number; heightMm: number;
  cols: number; rows: number;
  marginLeftMm: number; marginRightMm: number;
  marginTopMm: number; marginBottomMm: number; gutterMm: number;
}): string {
  const q = new URLSearchParams({
    paper:           opts.paper,
    widthMm:         String(opts.widthMm),
    heightMm:        String(opts.heightMm),
    cols:            String(opts.cols),
    rows:            String(opts.rows),
    marginLeftMm:    String(opts.marginLeftMm),
    marginRightMm:   String(opts.marginRightMm),
    marginTopMm:     String(opts.marginTopMm),
    marginBottomMm:  String(opts.marginBottomMm),
    gutterMm:        String(opts.gutterMm),
  });
  return `/api/v1/factory/machine-qr-batches/${batchId}/print?${q.toString()}`;
}

// ----- Factory QR batches (Stage-2 commissioning M2) -----

export function useQrBatchesList() {
  const getToken = useAccessToken();
  return {
    queryKey: ['qr-batches'] as const,
    listFn: () => apiFetch<QrBatchSummary[]>('/api/v1/factory/qr-batches', {}, getToken),
  };
}

export type GenerateQrBatchBody = {
  code: string;
  notes?: string | null;
  count: number;
  labelPaper?: string | null;
  labelWidthMm?: number | null;
  labelHeightMm?: number | null;
  colsPerSheet?: number | null;
  rowsPerSheet?: number | null;
  headerTemplateId?: string | null;
};

export function useGenerateQrBatch() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GenerateQrBatchBody) =>
      apiFetch<{ batch_id: string; code: string; count: number }>(
        '/api/v1/factory/qr-batches',
        {
          method: 'POST',
          body: JSON.stringify({
            Code:             body.code,
            Notes:            body.notes ?? null,
            Count:            body.count,
            LabelPaper:       body.labelPaper       ?? null,
            LabelWidthMm:     body.labelWidthMm     ?? null,
            LabelHeightMm:    body.labelHeightMm    ?? null,
            ColsPerSheet:     body.colsPerSheet     ?? null,
            RowsPerSheet:     body.rowsPerSheet     ?? null,
            HeaderTemplateId: body.headerTemplateId ?? null,
          }),
        }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['qr-batches'] }),
  });
}

/**
 * Build the URL for an SC QR batch print PDF. Mirrors the existing
 * stickerBatchPrintUrl shape so the UI can match line-for-line.
 */
export function scQrBatchPrintUrl(batchId: string, opts: {
  paper: string; widthMm: number; heightMm: number;
  cols: number; rows: number;
  marginLeftMm: number; marginRightMm: number;
  marginTopMm: number; marginBottomMm: number; gutterMm: number;
}): string {
  const q = new URLSearchParams({
    paper:           opts.paper,
    widthMm:         String(opts.widthMm),
    heightMm:        String(opts.heightMm),
    cols:            String(opts.cols),
    rows:            String(opts.rows),
    marginLeftMm:    String(opts.marginLeftMm),
    marginRightMm:   String(opts.marginRightMm),
    marginTopMm:     String(opts.marginTopMm),
    marginBottomMm:  String(opts.marginBottomMm),
    gutterMm:        String(opts.gutterMm),
  });
  return `/api/v1/factory/qr-batches/${batchId}/print?${q.toString()}`;
}

export function useQrBatchDetail(batchId: string | null) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['qr-batches', 'detail', batchId],
    queryFn:  () => apiFetch<QrBatchSummary>(`/api/v1/factory/qr-batches/${batchId}`, {}, getToken),
    enabled:  !!batchId,
  });
}

export function useQrBatchPairs(batchId: string | null) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['qr-batches', 'pairs', batchId],
    queryFn:  () => apiFetch<QrPairSummary[]>(`/api/v1/factory/qr-batches/${batchId}/qr-pairs`, {}, getToken),
    enabled:  !!batchId,
  });
}

export function useApplyQrBatchHeader() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ batchId, templateId }: { batchId: string; templateId: string }) =>
      apiFetch<void>(`/api/v1/factory/qr-batches/${batchId}/header-template`, {
        method: 'PUT',
        body: JSON.stringify({ TemplateId: templateId }),
      }, getToken),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['qr-batches'] });
      qc.invalidateQueries({ queryKey: ['qr-batches', 'detail', vars.batchId] });
    },
  });
}

export function useClearQrBatchHeader() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (batchId: string) =>
      apiFetch<void>(`/api/v1/factory/qr-batches/${batchId}/header-template`, {
        method: 'DELETE',
      }, getToken),
    onSuccess: (_d, batchId) => {
      qc.invalidateQueries({ queryKey: ['qr-batches'] });
      qc.invalidateQueries({ queryKey: ['qr-batches', 'detail', batchId] });
    },
  });
}

// ----- V2 site-controller lifecycle (May 2026) -----
// V1's order/ship/warehouse-receipt hooks are removed. Activate now
// takes operatorIdDeclared (binding moves from allocation time → commission
// time). New void / reverse-void / mark-defective flows per V2 §16.

export function useActivateScAtSite() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ qrId, operatorId, siteId, geofenceCoords }: {
      qrId: string;
      operatorId: string;
      siteId: string;
      geofenceCoords?: { lat: number; lng: number; accuracyMeters?: number | null } | null;
    }) =>
      apiFetch<{ siteControllerId: string; qrId: string;
                 operatorId: string; siteId: string;
                 status: string; commissionedAt: string; geofenceWarning: string | null }>(
        `/api/v1/provisioning/site-controllers/by-qr/${qrId}/activate`, {
          method: 'POST',
          body: JSON.stringify({
            OperatorIdDeclared: operatorId,
            SiteIdDeclared:     siteId,
            GeofenceCoords:     geofenceCoords ?? null,
          }),
        }, getToken),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['site-controllers'] });
    },
  });
}

export function useScLifecycleAction(scId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ action, reason }: {
      // V1 'block' is gone in V2 — replaced by the two-call void flow.
      action: 'decommission' | 'recommission';
      reason: string | null;
    }) =>
      apiFetch<{ site_controller_id: string; status: string }>(
        `/api/v1/site-controllers/${scId}/${action}`, {
          method: 'POST',
          body: JSON.stringify({ Reason: reason }),
        }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['site-controllers'] }),
  });
}

// ----- Portal restart controls (0363) — queue a host-restart command --------

type RestartResponse = { ok: boolean; commandId: string; commandType: string; message: string };

/** Force-restart a machine's gateway pod on its site controller. */
export function useRestartGateway(machineId: string) {
  const getToken = useAccessToken();
  return useMutation({
    mutationFn: () =>
      apiFetch<RestartResponse>(
        `/api/v1/machines/${machineId}/restart-gateway`, { method: 'POST' }, getToken),
  });
}

/** Restart the SC agent services, or reboot the SC. */
export function useScRestartAction() {
  const getToken = useAccessToken();
  return useMutation({
    mutationFn: ({ scId, action }: { scId: string; action: 'restart-services' | 'reboot' }) =>
      apiFetch<RestartResponse>(
        `/api/v1/site-controllers/${scId}/${action}`, { method: 'POST' }, getToken),
  });
}

// ----- V2 §16: two-call void / reverse-void with senior-tech approval -----

export function useRequestVoid(scId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ reason }: { reason: string }) =>
      apiFetch<{ site_controller_id: string; pending_void: boolean;
                 requested_at: string; reason: string }>(
        `/api/v1/site-controllers/${scId}/void`, {
          method: 'POST',
          body: JSON.stringify({ Reason: reason }),
        }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['site-controllers'] }),
  });
}

export function useCancelPendingVoid(scId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ site_controller_id: string; pending_void: boolean }>(
        `/api/v1/site-controllers/${scId}/void`, { method: 'DELETE' }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['site-controllers'] }),
  });
}

export function useApproveVoid(scId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ comment }: { comment?: string | null }) =>
      apiFetch<{ site_controller_id: string; status: string; voided_at: string }>(
        `/api/v1/site-controllers/${scId}/void/approve`, {
          method: 'POST',
          body: JSON.stringify({ Comment: comment ?? null }),
        }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['site-controllers'] }),
  });
}

export function useRequestVoidReversal(scId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ reason }: { reason: string }) =>
      apiFetch<{ site_controller_id: string; pending_void_reversal: boolean;
                 requested_at: string; reason: string }>(
        `/api/v1/site-controllers/${scId}/void/reverse`, {
          method: 'POST',
          body: JSON.stringify({ Reason: reason }),
        }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['site-controllers'] }),
  });
}

export function useCancelPendingVoidReversal(scId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ site_controller_id: string; pending_void_reversal: boolean }>(
        `/api/v1/site-controllers/${scId}/void/reverse`, { method: 'DELETE' }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['site-controllers'] }),
  });
}

export function useApproveVoidReversal(scId: string) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ comment }: { comment?: string | null }) =>
      apiFetch<{ site_controller_id: string; status: string; void_reversed_at: string }>(
        `/api/v1/site-controllers/${scId}/void/reverse/approve`, {
          method: 'POST',
          body: JSON.stringify({ Comment: comment ?? null }),
        }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['site-controllers'] }),
  });
}

// ----- SC SSH Phase-1 group I1: cert-reissue recovery approval -----

/**
 * Approver action-item feed — pending cert-reissue (device-cert recovery)
 * requests the caller can approve/reject. Gated server-side by
 * provisioning.site-controllers.cert-reissue-approve; operator approvers are
 * scoped to their own operator. Returns {queryKey, listFn} so it drops into
 * <RouteDataGrid/>. `refetchInterval` keeps the action-item list fresh.
 */
export function useCertReissueRequestsList(state: string = 'pending_approval') {
  const getToken = useAccessToken();
  return {
    queryKey: ['cert-reissue-requests', state] as const,
    listFn: () => apiFetch<PendingReissueRow[]>(
      `/api/v1/provisioning/cert-reissue-requests?state=${encodeURIComponent(state)}`, {}, getToken),
  };
}

export function useApproveCertReissue() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ scId, requestId, reason }: { scId: string; requestId: string; reason?: string | null }) =>
      apiFetch<{ requestId: string; state: string; scId: string }>(
        `/api/v1/provisioning/site-controllers/${scId}/cert-reissue-request/${requestId}/approve`, {
          method: 'POST',
          body: JSON.stringify({ Reason: reason ?? null }),
        }, getToken),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cert-reissue-requests'] });
      qc.invalidateQueries({ queryKey: ['site-controllers'] });
    },
  });
}

export function useRejectCertReissue() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ scId, requestId, reason }: { scId: string; requestId: string; reason?: string | null }) =>
      apiFetch<{ requestId: string; state: string; scId: string }>(
        `/api/v1/provisioning/site-controllers/${scId}/cert-reissue-request/${requestId}/reject`, {
          method: 'POST',
          body: JSON.stringify({ Reason: reason ?? null }),
        }, getToken),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cert-reissue-requests'] });
      qc.invalidateQueries({ queryKey: ['site-controllers'] });
    },
  });
}

// ----- SC SSH Phase-2 (Task C): CA rotation status + actions -----

/**
 * SC SSH Phase-2 (Task C2) — CA rotation status: per-kind generation rows with
 * device counts + the "still on v1" SC list. Gated server-side by
 * provisioning.site-controllers.ssh (operator+platform). `enabled` lets the
 * caller skip the fetch for non-holders so a 403 is never issued.
 */
export function useCaStatus(enabled: boolean = true) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['ssh-ca-status'] as const,
    queryFn: () => apiFetch<CaStatusResponse>('/api/v1/site-controllers/ssh/ca-status', {}, getToken),
    enabled,
    staleTime: 15_000,
  });
}

/** Rotate the operational SSH CA (B1). Platform/operator-admin (…ssh). */
export function useRotateOperationalCa() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<CaRotateResponse>('/api/v1/site-controllers/ssh/operational-ca/rotate', {
        method: 'POST', body: JSON.stringify({}),
      }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ssh-ca-status'] }),
  });
}

/** Rotate the commissioning SSH CA (C1). Platform-only (commissioning-ca.retire). */
export function useRotateCommissioningCa() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<CaRotateResponse>('/api/v1/site-controllers/ssh/commissioning-ca/rotate', {
        method: 'POST', body: JSON.stringify({}),
      }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ssh-ca-status'] }),
  });
}

/**
 * Portal-confirmed commissioning-CA retire (+ force override) — C3. Blocked
 * (409) by the server while in-service SCs have not reconciled the generation,
 * unless `force` is set (holdouts then need reflash).
 */
export function useRetireCommissioningCa() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ generation, force }: { generation: number; force?: boolean }) =>
      apiFetch<CommissioningRetireResponse>(
        `/api/v1/site-controllers/ssh/commissioning-ca/${generation}/retire${force ? '?force=true' : ''}`,
        { method: 'POST', body: JSON.stringify({}) }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ssh-ca-status'] }),
  });
}

/**
 * Force an A/B OS upgrade on a holdout SC (the reflash/upgrade nudge wired to the
 * "still on v1" list). NOTE: the force-os-upgrade endpoint itself is Task D — this
 * hook targets the agreed route so the button is wired now; if the endpoint is not
 * yet deployed the call returns 404 and the page surfaces it (see CaRotationPage).
 */
export function useForceOsUpgrade() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ scId }: { scId: string }) =>
      apiFetch<{ ok: boolean }>(
        `/api/v1/site-controllers/${scId}/force-os-upgrade`,
        { method: 'POST', body: JSON.stringify({}) }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ssh-ca-status'] }),
  });
}

/**
 * SC-detail break-glass console-login alarm feed. Gated server-side by
 * provisioning.site-controllers.console-bg. `enabled` lets the caller skip the
 * fetch for non-holders so a 403 is never issued.
 */
export function useConsoleBreakGlass(scId: string | null, enabled: boolean) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['console-break-glass', scId] as const,
    queryFn: () => apiFetch<BreakGlassResponse>(
      `/api/v1/site-controllers/${scId}/console-break-glass`, {}, getToken),
    enabled: enabled && !!scId,
  });
}

export function useMarkScDefective() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ qrId, reason }: { qrId: string; reason: string }) =>
      apiFetch<{ site_controller_id: string; qr_id: string; status: string }>(
        `/api/v1/provisioning/site-controllers/by-qr/${qrId}/mark-defective`, {
          method: 'POST',
          body: JSON.stringify({ Reason: reason }),
        }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['site-controllers'] }),
  });
}

// ----- Remote terminal (permission-gated recorded admin shell) -----

export interface OpenTerminalResult {
  session_id: string;
  browser_token: string;
  relay_path: string;   // /api/v1/terminal/relay/{id}/browser
  expires_at: string;
  recorded: boolean;
}

export function useOpenTerminalSession(scId: string) {
  const getToken = useAccessToken();
  return useMutation({
    mutationFn: ({ reason }: { reason: string }) =>
      apiFetch<OpenTerminalResult>(
        `/api/v1/site-controllers/${scId}/terminal/sessions`, {
          method: 'POST',
          body: JSON.stringify({ Reason: reason }),
        }, getToken),
  });
}

export function useKillTerminalSession(scId: string) {
  const getToken = useAccessToken();
  return useMutation({
    mutationFn: ({ sessionId }: { sessionId: string }) =>
      apiFetch<{ session_id: string; state: string }>(
        `/api/v1/site-controllers/${scId}/terminal/sessions/${sessionId}`,
        { method: 'DELETE' }, getToken),
  });
}

export interface TerminalSessionRow {
  session_id: string;
  site_controller_id: string;
  qr_id: string | null;
  requested_by: string | null;
  reason: string;
  state: string;
  created_at: string;
  started_at: string | null;
  ended_at: string | null;
  end_reason: string | null;
  duration_seconds: number | null;
  has_recording: boolean;
}

/** All terminal sessions in the caller's scope (newest first). */
export function useTerminalSessions() {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['terminal-sessions'],
    queryFn: () => apiFetch<TerminalSessionRow[]>('/api/v1/terminal/sessions', {}, getToken),
    refetchInterval: 30_000,
  });
}

// ----- Site controllers (Stage-2 commissioning M1) -----

export function useSiteControllersList(filter?: {
  operatorId?: string | null;
  siteId?: string | null;
  status?: string | null;
}) {
  const getToken = useAccessToken();
  const q = new URLSearchParams();
  if (filter?.operatorId) q.set('operatorId', filter.operatorId);
  if (filter?.siteId)     q.set('siteId',     filter.siteId);
  if (filter?.status)     q.set('status',     filter.status);
  const qs = q.toString();
  const url = qs ? `/api/v1/site-controllers?${qs}` : '/api/v1/site-controllers';
  return {
    queryKey: ['site-controllers', filter?.operatorId ?? null, filter?.siteId ?? null, filter?.status ?? null] as const,
    listFn: () => apiFetch<SiteControllerSummary[]>(url, {}, getToken),
  };
}

export function useSiteControllerDetail(id: string | null) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['site-controllers', 'detail', id],
    queryFn: () => apiFetch<SiteControllerDetail>(`/api/v1/site-controllers/${id}`, {}, getToken),
    enabled: !!id,
  });
}

// ---------- Issues (GitHub-backed bug/feature reporting) ----------

/**
 * Lists issues from GitHub via the API proxy. Returns the
 * <c>{queryKey, listFn}</c> shape so it can drop into
 * <RouteDataGrid /> directly. `state` defaults to 'open' on the
 * server; pass 'closed' or 'all' to widen.
 */
export function useIssuesList(state: 'open' | 'closed' | 'all' = 'open', label?: string) {
  const getToken = useAccessToken();
  const params = new URLSearchParams({ state });
  if (label) params.set('label', label);
  return {
    queryKey: ['issues', state, label ?? ''] as const,
    listFn:   () => apiFetch<Issue[]>(`/api/v1/issues?${params}`, {}, getToken),
  };
}

export function useIssue(number: number | null) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['issues', 'detail', number],
    queryFn:  () => apiFetch<Issue>(`/api/v1/issues/${number}`, {}, getToken),
    enabled:  number !== null && number > 0,
  });
}

/**
 * Multipart-form create. Uses raw `fetch` rather than `apiFetch` so the
 * browser sets the multipart boundary header automatically — apiFetch
 * forces `Content-Type: application/json` whenever a body is present.
 */
export function useCreateIssue() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateIssuePayload) => {
      const fd = new FormData();
      fd.append('title',       payload.title);
      fd.append('category',    payload.category);
      fd.append('priority',    payload.priority);
      fd.append('area',        payload.area);
      if (payload.areaLabel)         fd.append('areaLabel',        payload.areaLabel);
      if (payload.subArea)           fd.append('subArea',          payload.subArea);
      if (payload.subAreaLabel)      fd.append('subAreaLabel',     payload.subAreaLabel);
      if (payload.siteId)            fd.append('siteId',           payload.siteId);
      if (payload.machineSerial)     fd.append('machineSerial',    payload.machineSerial);
      fd.append('description', payload.description);
      if (payload.stepsToReproduce)  fd.append('stepsToReproduce', payload.stepsToReproduce);
      payload.images?.forEach(f => fd.append('images', f));

      const token = await getToken();
      const headers: Record<string, string> = { Accept: 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${apiBaseUrl}/api/v1/issues`, {
        method: 'POST',
        headers,
        body: fd,
      });
      if (!res.ok) {
        const text = await res.text();
        let parsed: { code?: string; message?: string } = {};
        try { parsed = JSON.parse(text); } catch { /* not JSON — keep raw */ }
        throw {
          status:  res.status,
          code:    parsed.code,
          message: parsed.message
            ?? `POST /api/v1/issues: HTTP ${res.status} ${res.statusText}${text ? ' — ' + text : ''}`,
        };
      }
      return res.json() as Promise<Issue>;
    },
    // Optimistic read-after-write (issue #30). GitHub's eventually-consistent
    // issues *list* endpoint omits a just-created issue from the immediate
    // refetch, so a plain invalidate would refetch a stale list that still
    // lacks the new row (the page only "fixed itself" on a fresh mount once
    // GitHub's index caught up). Instead we patch the server-returned issue
    // straight into every issues-list cache, then invalidate with
    // refetchType:'none' so the still-stale GitHub refetch can't clobber the
    // optimistic row before the next focus/mount reconciles it.
    onSuccess: (created) => {
      qc.setQueriesData<unknown>({ queryKey: ['issues'] }, (old) => {
        if (!Array.isArray(old)) return old;                                   // skip ['issues','detail',n] single-object cache
        if (old.some((i: unknown) => (i as Issue)?.number === created.number)) return old; // dedupe
        // Reproduce the IssueRow shape from IssuesPage.tsx (`id: String(i.number)`).
        // Keep these two in sync — see IssuesPage.tsx:55.
        return [{ ...created, id: String(created.number) }, ...old];
      });
      qc.invalidateQueries({ queryKey: ['issues'], refetchType: 'none' });
    },
  });
}

export function useEditIssue() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: EditIssuePayload) => {
      const fd = new FormData();
      fd.append('title',       payload.title);
      fd.append('category',    payload.category);
      fd.append('priority',    payload.priority);
      fd.append('area',        payload.area);
      if (payload.areaLabel)         fd.append('areaLabel',        payload.areaLabel);
      if (payload.subArea)           fd.append('subArea',          payload.subArea);
      if (payload.subAreaLabel)      fd.append('subAreaLabel',     payload.subAreaLabel);
      if (payload.siteId)            fd.append('siteId',           payload.siteId);
      if (payload.machineSerial)     fd.append('machineSerial',    payload.machineSerial);
      fd.append('description', payload.description);
      if (payload.stepsToReproduce)  fd.append('stepsToReproduce', payload.stepsToReproduce);
      if (payload.status)            fd.append('status',           payload.status);
      payload.keepAttachmentUrls.forEach(u => fd.append('keepAttachmentUrl', u));
      payload.images?.forEach(f => fd.append('images', f));

      const token = await getToken();
      const headers: Record<string, string> = { Accept: 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${apiBaseUrl}/api/v1/issues/${payload.number}`, {
        method: 'PATCH',
        headers,
        body: fd,
      });
      if (!res.ok) {
        const text = await res.text();
        let parsed: { code?: string; message?: string } = {};
        try { parsed = JSON.parse(text); } catch { /* not JSON — keep raw */ }
        throw {
          status:  res.status,
          code:    parsed.code,
          message: parsed.message
            ?? `PATCH /api/v1/issues/${payload.number}: HTTP ${res.status} ${res.statusText}${text ? ' — ' + text : ''}`,
        };
      }
      return res.json() as Promise<Issue>;
    },
    // Replace-by-number (issue #30). Same read-after-write reasoning as
    // useCreateIssue — swap the matching row for the server-returned object so
    // an edit updates the grid cell immediately, then a non-refetching
    // invalidate marks the list stale without a clobbering GitHub refetch.
    onSuccess: (updated) => {
      qc.setQueriesData<unknown>({ queryKey: ['issues'] }, (old) => {
        if (!Array.isArray(old)) return old;                                   // skip ['issues','detail',n] single-object cache
        // Reproduce the IssueRow shape from IssuesPage.tsx (`id: String(i.number)`).
        // Keep these two in sync — see IssuesPage.tsx:55.
        return old.map((i: unknown) =>
          (i as Issue)?.number === updated.number ? { ...updated, id: String(updated.number) } : i);
      });
      qc.invalidateQueries({ queryKey: ['issues'], refetchType: 'none' });
    },
  });
}

export function useSetIssueStatus() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ number, status }: { number: number; status: string }) =>
      apiFetch<void>(`/api/v1/issues/${number}/status`, {
        method: 'PATCH',
        body:   JSON.stringify({ Status: status }),
      }, getToken),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ['issues'] }); },
  });
}

export function useApprovePlan() {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (number: number) =>
      apiFetch<void>(`/api/v1/issues/${number}/approve-plan`, {
        method: 'POST',
      }, getToken),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ['issues'] }); },
  });
}

/**
 * Stage 3 §M-T8.5 — heartbeats for one SC, populates the Recharts
 * time-series AND the tabular subform on the SC detail dialog.
 *
 * Default window: last 48 h, last 3000 rows (≈ 50 h at 60 s cadence —
 * a comfortable margin). Pass `query` to widen / narrow. Hard-capped at
 * 5000 rows server-side. When `truncated=true` in the response, the
 * caller's window had more data than was returned.
 */
export function useSiteControllerHeartbeats(
  id: string | null,
  query?: import('./types').HeartbeatsQuery,
) {
  const getToken = useAccessToken();
  const params = new URLSearchParams();
  if (query?.from)  params.set('from',  query.from);
  if (query?.to)    params.set('to',    query.to);
  if (query?.limit) params.set('limit', String(query.limit));
  const qs = params.toString();
  return useQuery({
    queryKey: ['site-controllers', 'heartbeats', id, query?.from ?? '', query?.to ?? '', query?.limit ?? ''],
    queryFn: () => apiFetch<import('./types').HeartbeatsResponse>(
      `/api/v1/site-controllers/${id}/heartbeats${qs ? '?' + qs : ''}`, {}, getToken),
    enabled: !!id,
    refetchInterval: 60_000,
  });
}

/** SAS Analyzer permission code. */
export const SAS_ANALYZER_PERMISSION = 'provisioning.sas-analyzer';

/**
 * Decode pasted raw SAS 6.02 hex via POST /api/v1/sas/decode (pure/stateless).
 *  - 403 — no `provisioning.sas-analyzer` permission.
 */
export function decodeSas(paste: string, getToken: GetToken): Promise<import('./types').SasDecodeResult> {
  return apiFetch<import('./types').SasDecodeResult>(
    '/api/v1/sas/decode',
    { method: 'POST', body: JSON.stringify({ paste }) },
    getToken,
  );
}

// ---------- cash desk (stage 1 — plan Draft 1 W3) ----------

export function useCashDeskTerminals(siteId: string | null) {
  const getToken = useAccessToken();
  return {
    queryKey: ['cashdesk', 'terminals', siteId] as const,
    listFn: () => apiFetch<import('./types').CashDeskTerminal[]>(
      `/api/v1/cashdesk/sites/${siteId}/terminals`, {}, getToken),
  };
}

/** Approve (with name) or revoke a cash-desk terminal. Invalidate the
 *  site's terminal list on success. */
export function useCashDeskTerminalAction(siteId: string | null) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { terminalId: string; action: 'approve'; name: string }
                       | { terminalId: string; action: 'revoke' }) =>
      apiFetch<void>(
        `/api/v1/cashdesk/terminals/${input.terminalId}/${input.action}`,
        { method: 'POST', body: JSON.stringify(input.action === 'approve' ? { name: input.name } : {}) },
        getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cashdesk', 'terminals', siteId] }),
  });
}

export function useCashDeskOptions(siteId: string | null) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['cashdesk', 'options', siteId],
    queryFn: () => apiFetch<import('./types').CashDeskOptions>(
      `/api/v1/cashdesk/sites/${siteId}/options`, {}, getToken),
    enabled: !!siteId,
  });
}

export function useSaveCashDeskOptions(siteId: string | null) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (options: import('./types').CashDeskOptions) =>
      apiFetch<void>(`/api/v1/cashdesk/sites/${siteId}/options`,
        { method: 'PUT', body: JSON.stringify(options) }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cashdesk', 'options', siteId] }),
  });
}

/** Set (or replace) a user's cash-desk PIN (4–6 digits). The PIN is
 *  write-only — nothing is invalidated because nothing readable changes. */
export function useSetCashDeskPin(userId: string | null) {
  const getToken = useAccessToken();
  return useMutation({
    mutationFn: (pin: string) =>
      apiFetch<void>(`/api/v1/users/${userId}/cashdesk-pin`,
        { method: 'POST', body: JSON.stringify({ pin }) }, getToken),
  });
}

/** Cash Desk Activities — shifts by site (W10a endpoint; the page is
 *  fixture-tested until W10a wires it live). */
export function useCashDeskShifts(siteId: string | null) {
  const getToken = useAccessToken();
  return {
    queryKey: ['cashdesk', 'shifts', siteId] as const,
    // RouteDataGrid requires rows shaped { id } — alias shiftId onto id.
    listFn: () => apiFetch<import('./types').CashDeskShift[]>(
      `/api/v1/cashdesk/sites/${siteId}/shifts`, {}, getToken)
      .then((rows) => rows.map((r) => ({ ...r, id: r.shiftId }))),
  };
}

/** Manager-only RM amendment of a closed shift's counted figure. */
export function useAmendShiftClosing(siteId: string | null) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shiftId, closingCents, note }: {
      shiftId: string; closingCents: number; note: string | null;
    }) => apiFetch<{ shiftId: string; closingCents: number; varianceCents: number; originalClosingCents: number }>(
      `/api/v1/cashdesk/shifts/${shiftId}/amend-closing`,
      { method: 'POST', body: JSON.stringify({ closingCents, note }) }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cashdesk', 'shifts', siteId] }),
  });
}

export function useCashDeskShiftTransactions(shiftId: string | null) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['cashdesk', 'shift-transactions', shiftId],
    queryFn: () => apiFetch<import('./types').CashDeskShiftTransaction[]>(
      `/api/v1/cashdesk/shifts/${shiftId}/transactions`, {}, getToken),
    enabled: !!shiftId,
  });
}

// ---------- cash desk floor layout (W8a) ----------

export type CashDeskLayoutRow = {
  machineId: string;
  /** The GAME name where set (falls back to model/make/asset). */
  machineName: string;
  /** Cabinet make + model, e.g. "AGS Orion Curve". */
  cabinet: string | null;
  assetNumber: string;
  positionNo: number | null;
  x: number | null;
  y: number | null;
  rotation: number | null;
  hasPhoto: boolean;
  photoSha256: string | null;
  photoType: string | null;
};

export function useCashDeskLayout(siteId: string | null) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['cashdesk', 'layout', siteId],
    queryFn: () => apiFetch<CashDeskLayoutRow[]>(
      `/api/v1/cashdesk/sites/${siteId}/layout`, {}, getToken),
    enabled: !!siteId,
  });
}

export function useSaveCashDeskLayout(siteId: string | null) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entries: { machineId: string; positionNo: number; x: number; y: number; rotation: number }[]) =>
      apiFetch<void>(`/api/v1/cashdesk/sites/${siteId}/layout`,
        { method: 'PUT', body: JSON.stringify(entries) }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cashdesk', 'layout', siteId] }),
  });
}

/** Raw-body photo upload (PNG/JPEG/BMP ≤ 512 KB — owner ruling 4). */
/** The machine photo as a data URL for the layout editor's tiles + preview
 *  (photos are ≤512 KB — a data URL avoids objectURL lifecycle churn).
 *  Keyed by the sha so a re-upload busts the cache. */
export function useMachinePhoto(machineId: string | null, photoSha: string | null) {
  const getToken = useAccessToken();
  return useQuery({
    queryKey: ['cashdesk', 'machine-photo', machineId, photoSha],
    queryFn: async () => {
      const blob = await apiFetchBlob(`/api/v1/cashdesk/machines/${machineId}/photo`, getToken);
      return await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = () => reject(r.error);
        r.readAsDataURL(blob);
      });
    },
    enabled: !!machineId && !!photoSha,
    staleTime: Infinity,
  });
}

export function useUploadMachinePhoto(siteId: string | null) {
  const getToken = useAccessToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ machineId, file }: { machineId: string; file: File }) =>
      apiFetch<{ sha256: string }>(`/api/v1/cashdesk/machines/${machineId}/photo`,
        { method: 'POST', body: file, headers: { 'Content-Type': file.type } }, getToken),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cashdesk', 'layout', siteId] }),
  });
}
